import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownRehypeRawSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import { normalizePresentationMarkdown } from './panes/presentationMarkdown';
import {
  CITATION_SUP_CLASSNAME,
  CITATION_SUP_CLASSNAME_LIGHT,
  CITATION_SUP_STYLE,
  CITATION_SUP_STYLE_LIGHT,
  ensureCitationSupInlineStyles,
} from '@/shared/citationSupBadge';
import { Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { INVERSE_OVERLAY_SURFACE_CLASS } from '@/shared/inverse-overlay-surfaces';
import {
  ArchiveIcon,
  CheckIcon,
  ArrowClockwiseIcon,
  CrosshairIcon,
  DeviceMobileIcon,
  InfoIcon,
  MagicWandIcon,
  ListPlusIcon,
  CopyIcon,
  CaretCircleUpDownIcon,
  ThumbsUpIcon,
  ThumbsDownIcon,
  FunnelIcon,
  TranslateIcon,
  MagnifyingGlassIcon,
  FilePdfIcon,
  XIcon,
  DotsSixIcon,
} from "@phosphor-icons/react";
import { Spinner } from '@heroui/spinner';
import { Button } from '@tranquility-ai/theme-components/button';
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import {
  IsARefusal,
  CaptureError,
  getDownloadUrl,
  getMediaType,
  parseCitationPageFromSourceLike,
  resolvePreferredMediaDisplayForSource,
  recordStat,
  extractPathAfterBulk,
  getUiSourceFromChatSource,
  resolveCitationDisplayPage,
  getDispatchedFilePath,
  pathForAssistantScope,
  resolveFileInfoMediaDisplayUrl,
  resolveDisplayLookupUrl,
  resolveFullS3Url,
  buildTimelineCopySourceContext,
  getCaseDisplayName,
  getCaseInternalName,
  getFileInfoFromServer,
  getTokenInfo,
  getUserEmailForHeaders,
  isContainerLikeSource,
  sanitizeHttpUrlForBrowserLoad,
  isInlineJsonCitationDisplayable,
  isArchiveMemberSearchablePath,
  toSearchablePath,
} from '@/shared/functions';

import {
  citationPathsForFileInfoBatch,
  getCitationPreviewUrl,
  resolveCitationPrimaryDisplay,
} from '@/shared/citation-media-display';
import {
  chatCitationLabel,
  citationsVisibleInChat,
  isEvidenceOnlyCitation,
  isMobileEvidenceSource,
  visibleIndexForCitationSuperscript,
} from '@/components/mobile-citations/citation-evidence';
import { MobileEvidenceSourcePane } from '@/components/mobile-citations/MobileEvidenceSourcePane';
import { isMobileAnalysisAgentEnabled } from '@/lib/mobile-analysis-agent';
import { getCurrentFileInfoBatch, useWindowSize } from '../shared/clientFunctions';
import { FetchWithVersionCheck, trackEvent } from '@/shared/functions';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import { fetchChatHistory, postAssistantChat, signChatAttachment } from '@/shared/cors-assistant-api';
import {
  caseFolderNameBody,
  useCaseFolderName,
} from '@/hooks/useCaseFolderName';
import {
  cacheChatHistoryItem,
  getCachedChatHistoryItem,
  isFinalChatHistoryItem,
  cancelSharedChatHistoryPoll,
  isSessionAnswerSettled,
  markSessionAnswerSettled,
  removeCachedChatHistoryItem,
  scheduleSharedChatHistoryPoll,
  type CacheableChatHistoryItem,
} from '@/shared/chat-history-cache';
import {
  CHAT_HISTORY_FIRST_POLL_DELAY_MS,
  CHAT_HISTORY_POLL_INTERVAL_MS,
  parsePendingTurnIdentityFromAssistantResponse,
  shouldPollChatHistoryItem,
} from '@/shared/pending-chat-turn';
import { ChatAttachment, FeatureFlagTypes, FileSummary, GetEventsFromEntityService, MediaSource, MediaType, ScopesObject, Slide, parseChatAttachments } from '@/shared/types';
import {
  REFUSAL_MESSAGES,
  PASS_MESSAGES,
  SMALL_VIEWPORT_WIDTH,
  isDisplayableMediaType,
  ENRICHMENT_POLL_INTERVAL_MS,
  ENRICHMENT_POLL_TIMEOUT_MS,
  isEnrichmentHistoryPending,
} from '@/shared/constants';
import { LLMConfig, Source } from '../shared/types';
import { Card, CardHeader, CardBody, CardFooter } from "@heroui/card";
import { AttachmentList } from './attachment-list';
import { StyledList, StyledListItem } from './ui/styledListMarkdown';
import { CsamDetectionPill } from './ui/media/csam-detection-pill';
import { ContentModerationAckModal } from './ui/content-moderation-ack-modal';
import { AuthContext, FullScreenContext, StatsContext } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';
import { HotKeys } from '@/components/hot-keys';
import { SourceCard } from './sourceCard';
const AnswerItemFeedbackModal = dynamic(
  () => import('./answer-item-feedback-modal').then((m) => m.AnswerItemFeedbackModal),
  { ssr: false, loading: () => null },
);
const EventsReviewModal = dynamic(() => import('./events-review-modal'), {
  ssr: false,
  loading: () => null,
});
import { useMediaSourceShare } from './mediaSourceShare';
import { dispatchCopyToHistory } from './recently-copied';

import {
  encodePreliminaryMessageStrings,
  encodePreliminaryMessages,
  isPreliminaryMessagesText,
  parsePreliminaryMessages,
} from './preliminary-messages';
import { PATH_DISPLAY_WHITESPACE_CLASSNAME } from '@/shared/uiConstants';

function createAnswerTableComponents(lightBackground = false): Components {
  const border = lightBackground ? 'border-gray-300' : 'border-white/25';
  const header = lightBackground
    ? 'bg-gray-100 text-black'
    : 'bg-white/15 text-white';
  const cell = lightBackground ? 'text-black' : 'text-white';
  // The chat table scrolls. The export table keeps the same auto layout — a
  // fixed layout reflow blanks the capture — and only drops the scrollbar.
  // Wide tables are scaled to the page in fitWideExportTables.
  const frame = lightBackground
    ? 'my-3 w-full min-w-0 max-w-full pr-2'
    : 'my-3 w-full min-w-0 max-w-full overflow-x-auto custom-scrollbar2';
  const tableWidth = lightBackground ? 'w-full max-w-full' : 'w-max min-w-full';

  return {
    table: ({ children }) => (
      <div className={frame}>
        <table className={`${tableWidth} table-auto border-collapse border ${border} text-sm`}>
          {children}
        </table>
      </div>
    ),
    th: ({ children }) => (
      <th className={`min-w-32 max-w-[24rem] border p-2 text-left align-top font-semibold wrap-break-word ${border} ${header}`}>
        {children}
      </th>
    ),
    tr: ({ children }) => (
      <tr className={`border ${border} ${lightBackground ? 'even:bg-gray-50' : 'even:bg-white/5'}`}>
        {children}
      </tr>
    ),
    td: ({ children }) => (
      <td className={`min-w-32 max-w-[24rem] border p-2 align-top leading-relaxed wrap-break-word ${border} ${cell}`}>
        {children}
      </td>
    ),
  };
}

const answerTableComponents = createAnswerTableComponents();
const answerExportTableComponents = createAnswerTableComponents(true);

function isMarkdownAttachment(attachment: ChatAttachment): boolean {
  const filename = attachment.filename.toLowerCase();
  const mimeType = attachment.mime_type.toLowerCase().split(';')[0].trim();
  return (
    mimeType === 'text/markdown' ||
    mimeType === 'text/x-markdown' ||
    filename.endsWith('.md') ||
    filename.endsWith('.markdown')
  );
}

function markdownAttachmentDownloadUrl(attachment: ChatAttachment): string {
  const params = new URLSearchParams({
    s3_key: attachment.s3_key,
    filename: attachment.filename,
    mime_type: attachment.mime_type,
  });
  return `/api/chat-attachments/download?${params.toString()}`;
}

// Mirrors _MAX_OUTPUT_CHARS in edge-lambda-service telemetry_events.py. The
// backend only applies its own truncation when the payload carries a raw
// `answer` blob, so a payload sending `output` directly must arrive pre-capped.
const FEEDBACK_OUTPUT_MAX_CHARS = 20_000;

// The answer text to attach to a feedback event.
//
// Deliberately NOT the whole result object: that is the raw RAG response, whose
// `citations` array carries retrieved chunk text and dwarfs the answer itself.
// Telemetry discards every field except `output` anyway, and since the feedback
// row now records chat_key + epoch_time_secs the analytics view rehydrates the
// real answer from the chat-history archive — so the inline copy is only a
// display fallback. Sending the full blob pushed requests past a payload limit
// upstream of the telemetry handler, which rejected them with a 413 and lost the
// report entirely (no row is written, so nothing is recoverable afterwards).
function feedbackAnswerOutput(answer: unknown): string {
  let parsed: unknown = answer;
  if (typeof answer === 'string') {
    try {
      parsed = JSON.parse(answer);
    } catch {
      return answer.slice(0, FEEDBACK_OUTPUT_MAX_CHARS);
    }
  }
  const output =
    parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>).output
      : parsed;
  const text = typeof output === 'string' ? output : output == null ? '' : String(output);
  return text.slice(0, FEEDBACK_OUTPUT_MAX_CHARS);
}

function answerUsedEvidenceFilters(scopes: ScopesObject | undefined): boolean {
  if (!scopes) return false;
  return (
    (scopes.scopes?.length ?? 0) > 0 ||
    (scopes.files?.length ?? 0) > 0 ||
    (scopes.excludedScopes?.length ?? 0) > 0 ||
    (scopes.excludedFiles?.length ?? 0) > 0 ||
    (scopes.contentCategories?.length ?? 0) > 0
  );
}

interface AnswerItemProps {
  userId: string;
  caseId: string;
  answerIndex: number;
  question: string;
  answerText: string;
  sources: Source[];
  epochTimeSecs: number;
  onRedo: (question: string) => void;
  addToTimeline: (event: EventFromChat) => void;
  generateTimelineFromAnswer: (args: GenerateTimelineFromAnswerArgs) => void | Promise<void>;
  timelineGenerationStatus?: AnswerTimelineGenerationStatus;
  addMultipleToTimeline: (events: Slide[], useEnrichment: boolean, edit: boolean) => void;
  applyAutogeneratedTimelineFromAnswer: (answerText: string, epochTimeSecs: number) => void;
  isAutogeneratedTimelineAppliedForAnswer: (epochTimeSecs: number) => boolean;
  isAutogeneratedTimelineApplyingForAnswer: (epochTimeSecs: number) => boolean;
  isEditableTimeline: boolean;
  featureFlags: { [key: string]: FeatureFlagTypes };
  resultForFeedback: any;
  scopesObject: ScopesObject;
  setCurrentScopesAndFiles: (scopes: ScopesObject) => void;
  addSourceToScope: (path: string) => void;
  onSendToQuery?: (text: string) => void;
  onFilterEvidence?: (text: string) => void;
  setIsExpanded?: React.Dispatch<React.SetStateAction<boolean>>;
  extractionJob: any;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  getEventsFromEntityService: GetEventsFromEntityService;
  snapToTop: () => void;
  pendingPlaceholder?: boolean;
  /** Fired once after this row paints (used to collapse the question bar after submit). */
  onDisplayed?: () => void;
  /** Fired once when this row reaches a terminal status (completed or error). */
  onAnswerSettled?: () => void;
  /**
   * Generated-file chips for this turn, carried on the already-loaded history
   * row. The per-row `getAnswerFromChatHistory` fetch is short-circuited for
   * rows that are already `completed` on load, so without this prop chips
   * would only ever appear during live generation and vanish on reload
   * (TR-1453).
   */
  attachments?: ChatAttachment[];
  /**
   * Read-only presentation (e.g. the analytics/feedback archived-answer view).
   * Hides all mutating actions: create/generate timeline from the response,
   * thumbs up/down feedback, and (via SourceCard) add-source-to-scope and
   * create-timeline-event-from-source. Non-mutating actions (copy, download,
   * source viewer/maximize) stay available.
   */
  readOnly?: boolean;
  /**
   * Fill the parent's height instead of sizing to the chat viewport. The item
   * itself then never scrolls; the question, answer body, sources list and
   * source preview each scroll on their own. The parent must have a definite
   * height (e.g. the analytics session viewer).
   */
  fillHeight?: boolean;
  /**
   * Replaces the default attachment download (assistant-service signs only the
   * caller's own attachments) and shows attachments regardless of the
   * `chatAttachments` flag. Used by the analytics view for other users' turns.
   */
  onAttachmentDownload?: (attachment: ChatAttachment) => void;
}

export interface Answer {
  question: string;
  answerText: string;
  sources: Source[];
  sessionId: string | null;
  timeSaved: number;
  resultForFeedback?: any;
  scopesObject: ScopesObject;
  epochTimeSecs: number;
  /** Optimistic row appended locally until `/chat/history` includes the new turn. */
  pendingPlaceholder?: boolean;
  /** Generated-file attachments carried on the history row (TR-1453). */
  attachments?: ChatAttachment[];
  /**
   * Read-only presentation (e.g. the analytics/feedback archived-answer view).
   * Hides all mutating actions: create/generate timeline from the response,
   * thumbs up/down feedback, and (via SourceCard) add-source-to-scope and
   * create-timeline-event-from-source. Non-mutating actions (copy, download,
   * source viewer/maximize) stay available.
   */
  readOnly?: boolean;
};

export interface EventFromChat {
  answerText: string;
  sources: Source[];
  autogen: boolean;
  epochTimeSecs: number;
  slideTitle?: string;
  slideDescription?: string;
  media?: MediaSource[];
}

export type GenerateTimelineFromAnswerArgs = {
  question: string;
  answerText: string;
  sources: Source[];
  epochTimeSecs: number;
};

export type AnswerTimelineGenerationStatus = 'idle' | 'pending' | 'completed';

function getFilename(source: Source): string {
  return chatCitationLabel(source);
}

/** Only audio/video use time indices in source labels; start/end are ignored for all other types. */
function audioVideoTimeSuffix(source: Source, t?: TranslateFn): string {
  const parts: string[] = [];
  const start = source.sourceContext?.startTime;
  const end = source.sourceContext?.endTime;
  if (start) parts.push(t?.('sources.rowStart', { time: start }) ?? `Start: ${start}`);
  if (end) parts.push(t?.('sources.rowEnd', { time: end }) ?? `End: ${end}`);
  return parts.length ? `, ${parts.join(', ')}` : '';
}

/**
 * When the sources panel is open, {@link sourceMediaType} is known. When closed, we only call
 * {@link getMediaType} if page or time fields are set so labels stay cheap for typical rows.
 */
function sourceListLabelParts(
  source: Source,
  sourceMediaType: MediaType | null,
  t?: TranslateFn,
): { showPageNumber: boolean; timeSuffix: string; pageLabel: string; pageValue?: number } {
  // PowerPoint citations are paged like documents but the page is a slide, so
  // label it "Slide" instead of "page".
  const pageLabelFor = (mediaType: MediaType | null): string =>
    mediaType === 'pptx'
      ? (t?.('chat.answer.slide') ?? 'Slide')
      : (t?.('chat.answer.page') ?? 'page');
  // Resolve the page/slide from the structured sourceContext fields too, not just
  // the legacy stringy ``source.page``. PowerPoint (and newer paged) citations can
  // carry the slide via ``start_slide`` / ``slide_numbers`` / ``source_page_number``
  // before ``getUiSourceFromChatSource`` back-fills ``source.page``; keying off only
  // ``source.page`` here would drop the suffix during that window.
  const resolvedPage = resolveCitationDisplayPage(source);
  const hasPage = resolvedPage !== undefined;
  const displayedMediaUrl = resolvePreferredMediaDisplayForSource(
    source,
    source.fileInfo,
  ).mediaUrl;
  const citationAssetUrl = source.sourceFilePath || source.sourceUrl;
  const usesAlternateDisplayAsset =
    !!displayedMediaUrl &&
    !!citationAssetUrl &&
    displayedMediaUrl !== citationAssetUrl;
  if (sourceMediaType !== null) {
    const isAv = sourceMediaType === 'audio' || sourceMediaType === 'video';
    let showPageNumber = false;
    if (!isAv && hasPage) {
      showPageNumber = true;
      if (usesAlternateDisplayAsset) {
        showPageNumber = false;
      }
    }
    const timeSuffix = isAv ? audioVideoTimeSuffix(source, t) : '';
    return { showPageNumber, timeSuffix, pageLabel: pageLabelFor(sourceMediaType), pageValue: resolvedPage };
  }
  // Panel closed: infer type only when page or time fields could affect the label.
  const needInferredMediaType =
    hasPage ||
    !!source.sourceContext?.startTime ||
    !!source.sourceContext?.endTime;
  const inferredMediaType = needInferredMediaType ? getMediaType(source, source.fileInfo) : null;
  if (inferredMediaType === 'audio' || inferredMediaType === 'video') {
    return {
      showPageNumber: false,
      timeSuffix: audioVideoTimeSuffix(source, t),
      pageLabel: t?.('chat.answer.page') ?? 'page',
    };
  }
  let showPageNumber = hasPage;
  if (showPageNumber && usesAlternateDisplayAsset) {
    showPageNumber = false;
  }
  return { showPageNumber, timeSuffix: '', pageLabel: pageLabelFor(inferredMediaType), pageValue: resolvedPage };
}

function getSourceIndexFromCitationSupChildren(children: React.ReactNode): number | null {
  const text = React.Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('')
    .trim();
  const citationNumber = parseInt(text, 10);
  if (!Number.isFinite(citationNumber) || citationNumber < 1) {
    return null;
  }
  return citationNumber - 1;
}

function applyCitationPrimaryDisplayToMediaSource(
  sourceObject: MediaSource,
  source: Source,
  primaryUrl: string | undefined,
  fileInfo: FileSummary | null,
): MediaSource {
  if (fileInfo) {
    sourceObject.fileInfo = fileInfo;
  }
  if (!primaryUrl) {
    return sourceObject;
  }

  const canonicalSourceUrl = source.sourceFilePath || source.sourceUrl;
  const previewFilePath =
    typeof source.previewFilePath === 'string' ? source.previewFilePath.trim() : '';
  const dispatchedFilePath = getDispatchedFilePath(source);

  sourceObject.mediaUrl = primaryUrl;
  sourceObject.mediaSrc = primaryUrl;
  sourceObject.mediaContext = {
    ...(sourceObject.mediaContext || sourceObject.sourceContext || {}),
    ...(primaryUrl !== canonicalSourceUrl ? { previewUrl: primaryUrl } : {}),
  };

  if (primaryUrl === previewFilePath || getCitationPreviewUrl(source) === primaryUrl) {
    sourceObject.previewFilePath = primaryUrl;
  } else if (primaryUrl === dispatchedFilePath) {
    sourceObject.dispatchedFilePath = primaryUrl;
  }

  return sourceObject;
}

/**
 * Resolve the path to use for a source's content-moderation file-info lookup.
 *
 * Archive / UFDR citations point ``sourceFilePath`` at the *container* (e.g. a
 * ``.zip``), but CM is flagged on the extracted child, not the container. For
 * container-like sources we resolve the actually-cited extract path (the same
 * path that drives the displayed media) so the lookup hits the flagged
 * subdocument. Non-container sources keep using ``sourceFilePath`` unchanged.
 */
function resolveCmLookupPath(source: Source | MediaSource): string | undefined {
  const preferred = resolvePreferredMediaDisplayForSource(source, source.fileInfo);
  const extractPath =
    isContainerLikeSource(source, source.fileInfo) &&
    (preferred.selectedBy === 'previewFilePath' || preferred.selectedBy === 'dispatchedFilePath')
      ? preferred.mediaUrl
      : undefined;
  return extractPath || source.sourceFilePath || undefined;
}

/**
 * Turn one chat citation into a media row.
 *
 * ``prefetchedFileInfo`` comes from one {@link getCurrentFileInfoBatch} over
 * the whole citation list. Do not call ``getCurrentFileInfo`` or
 * ``GET /files/file-info`` here — that would put the list back on one GET per
 * file. Evidence-only citations never need a lookup.
 */
async function processCitationForAnswerItem(
  source: Source,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  jsonMediaDisplayEnabled = false,
  prefetchedFileInfo?: Map<string, FileSummary | null>,
): Promise<MediaSource> {
  if (isEvidenceOnlyCitation(source)) {
    return getUiSourceFromChatSource(source);
  }
  const sourceObject = getUiSourceFromChatSource(source);
  const { primaryUrl, fileInfo } = await resolveCitationPrimaryDisplay(
    source,
    cellId,
    accessToken,
    jsonMediaDisplayEnabled,
    prefetchedFileInfo,
  );
  const result = applyCitationPrimaryDisplayToMediaSource(
    sourceObject,
    source,
    primaryUrl,
    fileInfo,
  );
  // Content-only JSON citations (flag on, no displayable file URL): mark as json so
  // SourceCard / MediaDisplay can render the inline excerpt without a media URL.
  if (!primaryUrl) {
    const preferred = resolvePreferredMediaDisplayForSource(
      source,
      fileInfo ?? source.fileInfo ?? null,
    );
    if (
      isInlineJsonCitationDisplayable(source, preferred.mediaType, jsonMediaDisplayEnabled)
    ) {
      result.mediaType = 'json';
    }
  }
  return result;
}

function buildEventMediaFromSources(
  sources: Source[],
  jsonMediaDisplayEnabled = false,
): MediaSource[] {
  const seen = new Set<string>();
  const mediaRows: MediaSource[] = [];
  for (const source of sources) {
    const preferred = resolvePreferredMediaDisplayForSource(source, source.fileInfo);
    const asMedia = source as MediaSource;
    // processCitationForAnswerItem may have already attached a proxyable
    // mediaUrl/mediaSrc while preferred still points at a virtual archive path.
    const existingUrl = (asMedia.mediaUrl || asMedia.mediaSrc || '').trim();
    const existingType = existingUrl
      ? getMediaType(
          {
            mediaType: asMedia.mediaType || 'unknown',
            mediaUrl: existingUrl,
          } as MediaSource,
          source.fileInfo,
        )
      : ('unknown' as MediaType);
    const existingDisplayable =
      !!existingUrl &&
      !isArchiveMemberSearchablePath(existingUrl) &&
      isDisplayableMediaType(
        existingType !== 'unknown' ? existingType : asMedia.mediaType,
        { jsonMediaDisplay: jsonMediaDisplayEnabled },
      );
    const preferredDisplayable =
      !!preferred.mediaUrl &&
      !isArchiveMemberSearchablePath(preferred.mediaUrl) &&
      isDisplayableMediaType(preferred.mediaType, { jsonMediaDisplay: jsonMediaDisplayEnabled });
    const hasDisplayableUrl = preferredDisplayable || existingDisplayable;
    const contentOnlyJson =
      !hasDisplayableUrl &&
      isInlineJsonCitationDisplayable(
        source,
        preferred.mediaType,
        jsonMediaDisplayEnabled,
      );
    if (!hasDisplayableUrl && !contentOnlyJson) {
      continue;
    }

    const mediaType = preferredDisplayable
      ? preferred.mediaType
      : existingDisplayable
        ? ((existingType !== 'unknown' ? existingType : asMedia.mediaType) || preferred.mediaType)
        : 'json';
    const mediaUrl = preferredDisplayable
      ? preferred.mediaUrl!
      : existingDisplayable
        ? existingUrl
        : '';
    const pageNumber = parseCitationPageFromSourceLike(source);
    const contentKey = (source.content || '').trim().slice(0, 96);
    const canonicalSourceUrl = source.sourceFilePath || source.sourceUrl || '';
    const key = [
      mediaType,
      mediaUrl || `content:${contentKey}`,
      // Keep distinct content-only citations that share an excerpt prefix.
      !mediaUrl ? `src:${canonicalSourceUrl}` : '',
      `page:${pageNumber}`,
      `start:${source.sourceContext?.startTime ?? ''}`,
      `end:${source.sourceContext?.endTime ?? ''}`,
    ].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    const mediaContext = {
      ...(source.sourceContext || {}),
      pageNumber,
      ...(mediaUrl && mediaUrl !== canonicalSourceUrl ? { previewUrl: mediaUrl } : {}),
    };
    mediaRows.push({
      mediaType,
      ...(mediaUrl ? { mediaUrl, mediaSrc: mediaUrl } : {}),
      sourceFilePath: source.sourceFilePath,
      sourceUrl: canonicalSourceUrl,
      originalUrl: canonicalSourceUrl,
      fileInfo: source.fileInfo,
      // Preserve citation excerpt so MediaDisplay can render / toggle JSON↔markdown.
      content: source.content,
      ...(preferredDisplayable && preferred.selectedBy === 'previewFilePath'
        ? { previewFilePath: preferred.mediaUrl }
        : preferredDisplayable && preferred.selectedBy === 'dispatchedFilePath'
          ? { dispatchedFilePath: preferred.mediaUrl }
          : {}),
      mediaContext,
    } as MediaSource);
  }
  return mediaRows;
}

async function handleCopyToClipboard(
  question: string,
  answerIndex: number,
  sources: Source[],
  answerText: string,
  t?: TranslateFn,
): Promise<boolean> {
  const questionLabel = t?.('chat.answer.question') ?? 'Question';
  const sourcesLabel = t?.('chat.answer.sources') ?? 'Sources';
  const pageLabel = t?.('chat.answer.page') ?? 'page';
  const answer = document.getElementById(`answerText_${answerIndex}`);
  if (!!answer && !!question) {
    // Inline citation-badge styles so paste targets keep the grey pill without our CSS.
    let html = ensureCitationSupInlineStyles(
      `<h3>${questionLabel}: ${question}</h3>${answer.innerHTML}`,
    );
    let plain = `${questionLabel}: ${question}\n\n${answerText}`;
    if (sources && sources.length > 0) {
      html += `<h3>${sourcesLabel}:</h3><ol>`;
      plain += `\n\n${sourcesLabel}:\n`;
      for (let i = 0; i < sources.length; i++) {
        const source = sources[i];
        const path = source.truncatedFilePath;
        const sourceMediaType = getMediaType(source, source.fileInfo);
        const isAv = sourceMediaType === 'audio' || sourceMediaType === 'video';
        const page = !isAv && source.page ? `, ${pageLabel}: ${source.page}` : '';
        const timePart = isAv ? audioVideoTimeSuffix(source, t) : '';
        html += `<li>${path}${page}${timePart}</li>`;
        plain += `${i + 1}. ${path}${page}${timePart}\n`;
      }
      html += '</ol>';
    };
    try {
      const clipboardItem = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([clipboardItem])
        .then(() => {
          dispatchCopyToHistory(plain);
          return Promise.resolve(true);
        })
        .catch(error => {
          return Promise.resolve(false);
        })
    } catch (error) {
      return Promise.resolve(false);
    }
  }
  return Promise.resolve(true);
}

interface ContextMenuPosition {
  x: number;
  y: number;
}

const languageOptions = [
  { code: 'en', name: 'English', englishName: 'English' },
  { code: 'el', name: 'Ελληνικά', englishName: 'Greek' },
  { code: 'ar', name: 'العربية', englishName: 'Arabic' },
  { code: 'zh', name: '简体中文', englishName: 'Chinese (Simplified)' },
  { code: 'hi', name: 'हिन्दी', englishName: 'Hindi' },
  { code: 'ru', name: 'Русский', englishName: 'Russian' },
  { code: 'es', name: 'Español', englishName: 'Spanish' },
  { code: 'de', name: 'Deutsch', englishName: 'German' }
];

type TranslateMenuMode = 'quick' | 'chat' | 'result';
type QuickTranslateStatus = 'idle' | 'loading' | 'done' | 'error';

const TRANSLATE_MODAL_MARGIN_PX = 8;
const TRANSLATE_MODAL_LANG_SIZE = { width: 180, height: 240 };
const TRANSLATE_MODAL_RESULT_SIZE = { width: 320, height: 360 };

function clampTranslateModalPosition(
  x: number,
  y: number,
  width: number,
  height: number,
  margin = TRANSLATE_MODAL_MARGIN_PX,
): { x: number; y: number } {
  const maxX = Math.max(margin, window.innerWidth - width - margin);
  const maxY = Math.max(margin, window.innerHeight - height - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

/**
 * Parse assistant replies shaped like `SPANISH: <translated text>`.
 * LANGUAGE is the detected source language; the rest is the translation.
 */
function parseQuickTranslatePrefixedResult(raw: string): {
  detectedLanguage: string | null;
  translation: string;
} {
  const cleaned = sanitizeQuickTranslateText(raw);
  // Allow optional markdown/HTML wrapping around the LANGUAGE: prefix.
  const match = cleaned.match(
    /^(?:<(?:p|div|span)\b[^>]*>\s*)*([A-Za-z][A-Za-z\s\-']{0,40}?)\s*:\s*([\s\S]+)$/i,
  );
  if (!match) {
    return { detectedLanguage: null, translation: cleaned };
  }
  const detectedLanguage = match[1].trim().replace(/\s+/g, ' ');
  let translation = match[2].trim();
  // Close any opening tag that rode along with the prefix (e.g. `<p>SPANISH: …`).
  translation = sanitizeQuickTranslateText(translation);
  if (!detectedLanguage || !translation) {
    return { detectedLanguage: null, translation: cleaned };
  }
  return { detectedLanguage, translation };
}

function extractAssistantOutputText(item: {
  answer?: unknown;
  status?: unknown;
}, failureMessage = 'Translation failed.'): string {
  if (item.status === 'error') {
    if (typeof item.answer === 'string' && item.answer.trim() !== '') {
      try {
        const parsed = JSON.parse(item.answer) as { output?: string };
        const output = typeof parsed.output === 'string' ? parsed.output.trim() : '';
        if (output) return sanitizeQuickTranslateText(output);
      } catch {
        // fall through
      }
    }
    return failureMessage;
  }
  if (typeof item.answer !== 'string' || item.answer.trim() === '') {
    return '';
  }
  try {
    const parsed = JSON.parse(item.answer) as { output?: string };
    const output = typeof parsed.output === 'string' ? parsed.output.trim() : '';
    return sanitizeQuickTranslateText(output);
  } catch {
    return sanitizeQuickTranslateText(item.answer.trim());
  }
}

/**
 * Clean quick-translate source/result text:
 * - strip trailing numbered citation `<sup>…</sup>` tags (e.g. `<sup>1</sup>`, `<sup>2</sup>`)
 *   including when they sit just before a closing `</p>`, and HTML-escaped forms
 * - strip trailing empty `<p></p>` tags
 * - strip trailing mismatched `</p><p>` pairs (wrong order near the end)
 */
function sanitizeQuickTranslateText(text: string): string {
  let cleaned = text.trim();
  // Repeat until stable so mixed trailing `<sup>` / empty `<p>` sequences all peel off.
  let previous = '';
  while (previous !== cleaned) {
    previous = cleaned;
    cleaned = cleaned
      // `<sup>1</sup>` immediately before `</p>` (common when the model wraps the reply)
      .replace(
        /(?:\s*<sup\b[^>]*>\s*\d+(?:\s*[,，]\s*\d+)*\s*<\/sup>)+(?=\s*<\/p>)/gi,
        '',
      )
      // HTML-escaped `<sup>1</sup>` immediately before escaped/plain `</p>`
      .replace(
        /(?:\s*&lt;sup\b[^&]*&gt;\s*\d+(?:\s*[,，]\s*\d+)*\s*&lt;\/sup&gt;)+(?=\s*(?:&lt;\/p&gt;|<\/p>))/gi,
        '',
      )
      // trailing numbered superscripts at end of string
      .replace(/(?:\s*<sup\b[^>]*>\s*\d+(?:\s*[,，]\s*\d+)*\s*<\/sup>)+\s*$/gi, '')
      .replace(/(?:\s*&lt;sup\b[^&]*&gt;\s*\d+(?:\s*[,，]\s*\d+)*\s*&lt;\/sup&gt;)+\s*$/gi, '')
      // mismatched `</p><p>` near the end (wrong order; often followed by empty content / another close)
      .replace(/(?:\s*<\/p>\s*<p\b[^>]*>)+(?:\s*(?:<\/p>|(?:\s|&nbsp;|<br\s*\/?>)+))*\s*$/gi, '')
      .replace(/(?:\s*&lt;\/p&gt;\s*&lt;p\b[^&]*&gt;)+(?:\s*(?:&lt;\/p&gt;|(?:\s|&nbsp;|&lt;br\s*\/?&gt;)+))*\s*$/gi, '')
      // orphan trailing closer left after splitting a wrapping `<p>LANG: …</p>` reply
      .replace(/\s*<\/p>\s*$/gi, '')
      .replace(/\s*&lt;\/p&gt;\s*$/gi, '')
      // trailing empty paragraphs
      .replace(/(?:\s*<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>)+\s*$/gi, '')
      .replace(/(?:\s*&lt;p\b[^&]*&gt;(?:\s|&nbsp;|&lt;br\s*\/?&gt;)*&lt;\/p&gt;)+\s*$/gi, '')
      .trimEnd();
  }
  // Stripping `</p><p>` can leave a dangling opening `<p>` with no closer.
  if (/^<p\b[^>]*>/i.test(cleaned) && !/<\/p>/i.test(cleaned)) {
    cleaned = cleaned.replace(/^<p\b[^>]*>\s*/i, '');
  }
  if (/^&lt;p\b[^&]*&gt;/i.test(cleaned) && !/&lt;\/p&gt;/i.test(cleaned)) {
    cleaned = cleaned.replace(/^&lt;p\b[^&]*&gt;\s*/i, '');
  }
  return cleaned.trim();
}

interface PdfExportSnapshot {
  question: string;
  answerText: string;
  sources: Source[];
}

// Letter width minus the 5 mm export margins, converted at the CSS standard
// 96 px/in. Keeping this fixed stabilizes wrapping without capping the
// content-driven height of multi-page answers.
const ANSWER_PDF_VIEWPORT = {
  width: 778,
} as const;

// Room for the export frame's right padding and the table border, so a scaled
// table's last column is not sitting on the capture edge.
const ANSWER_PDF_TABLE_FIT_WIDTH = ANSWER_PDF_VIEWPORT.width - 16;

// `min-w-32` keeps seven or more columns wider than the capture, and switching
// those cells to a fixed layout blanks every page. Scale the already-laid-out
// table instead; the auto layout itself stays untouched.
function fitWideExportTables(root: HTMLElement): () => void {
  const restores: Array<() => void> = [];
  root.querySelectorAll('table').forEach((table) => {
    const naturalWidth = table.scrollWidth;
    if (naturalWidth <= ANSWER_PDF_TABLE_FIT_WIDTH) {
      return;
    }
    const frame = table.parentElement;
    if (!frame) {
      return;
    }
    const scale = ANSWER_PDF_TABLE_FIT_WIDTH / naturalWidth;
    const naturalHeight = table.offsetHeight;
    const previous = {
      transform: table.style.transform,
      transformOrigin: table.style.transformOrigin,
      height: frame.style.height,
      overflow: frame.style.overflow,
    };
    table.style.transformOrigin = 'top left';
    table.style.transform = `scale(${scale})`;
    frame.style.height = `${Math.ceil(naturalHeight * scale)}px`;
    frame.style.overflow = 'hidden';
    restores.push(() => {
      table.style.transform = previous.transform;
      table.style.transformOrigin = previous.transformOrigin;
      frame.style.height = previous.height;
      frame.style.overflow = previous.overflow;
    });
  });
  return () => {
    restores.forEach((restore) => restore());
  };
}

export const AnswerItem: React.FC<AnswerItemProps> = ({
  userId,
  caseId,
  answerIndex,
  question,
  answerText,
  sources,
  epochTimeSecs,
  onRedo,
  addToTimeline,
  generateTimelineFromAnswer,
  timelineGenerationStatus = 'idle',
  addMultipleToTimeline,
  applyAutogeneratedTimelineFromAnswer,
  isAutogeneratedTimelineAppliedForAnswer,
  isAutogeneratedTimelineApplyingForAnswer,
  isEditableTimeline,
  featureFlags,
  resultForFeedback,
  scopesObject,
  setCurrentScopesAndFiles,
  addSourceToScope,
  onSendToQuery,
  onFilterEvidence,
  setIsExpanded,
  extractionJob,
  createEventsFromEntityEvents,
  getEventsFromEntityService,
  snapToTop,
  pendingPlaceholder = false,
  onDisplayed,
  onAnswerSettled,
  attachments: attachmentsProp,
  readOnly = false,
  fillHeight = false,
  onAttachmentDownload,
}) => {
  const t = useTimePilotT();
  const mobileAnalysisEnabled = isMobileAnalysisAgentEnabled(featureFlags);
  const { width } = useWindowSize();
  const isSmallScreen = width !== undefined && width < SMALL_VIEWPORT_WIDTH;
  // Rename-safe identifier for this row's case, declared up here because both a
  // `/chat` POST and a `/chat/history` read below need it. Sent alongside
  // `selectedCase` so retrieval filters on the case's real tag rather than a
  // `case:<display name>` guess, and alongside `caseId` so the history read hits
  // the partition the write used. See `useCaseFolderName`.
  const caseFolderName = useCaseFolderName(caseId);
  const mediaSourceShare = useMediaSourceShare();
  const { isFullscreen, setFullscreenMediaItem } = useContext(FullScreenContext);

  const [sourcesMediaOpen, setSourcesMediaOpen] = useState(false);
  const [selectedSourceIndex, setSelectedSourceIndex] = useState<number | null>(null);
  // origUrl: undefined = not yet fetched, null = fetch failed/returned nothing, string = valid URL
  const [cmDataMap, setCmDataMap] = useState<Map<number, { type: string; obfUrl: string | null; origUrl: string | null | undefined }>>(new Map());
  // Tag-level gate for CSAM labels, obfuscated preview, and the acknowledgement
  // modal. When off, sources are treated as non-CSAM everywhere downstream because
  // `cmDataMap` stays empty. Descriptions are gated separately at limited-user.
  const { canSeeCsamTags, canRevealOriginal } = useCsamAccess();
  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  const [contentModerationAckIndex, setContentModerationAckIndex] = useState<number | null>(null);
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  // Non-verifiers can never leave the obfuscated view: pin to `true` regardless of
  // the raw `showObfuscatedPreview` state so a stale/false value (e.g. left over
  // from a prior verifier session or a mid-session flag change) can't reveal the
  // original — or trigger the audited `bypass_cm` fetch — in citation previews.
  // Every CM preview decision (render URL, bypass fetch, remount key, SourceCard
  // prop) must read this, not the raw state.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;
  const [copied, setCopied] = useState(false);
  const [copiedSource, setCopiedSource] = useState(-1);
  const [redoClicked, setRedoClicked] = useState(false);
  const [refusalMessages, setRefusalMessages] = useState<string[]>(REFUSAL_MESSAGES);
  const [passMessages, setPassMessages] = useState<string[]>(PASS_MESSAGES);
  const [leftCardWidth, setLeftCardWidth] = useState<number | null>(null);
  const [rightCardWidth, setRightCardWidth] = useState<number | null>(null);
  const [positiveFeedbackClicked, setPositiveFeedbackClicked] = useState(false);
  const [negativeFeedbackClicked, setNegativeFeedbackClicked] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [dataForChatResultFeedback, setDataForChatResultFeedback] = useState<any>(null);
  const [applyScopesClicked, setApplyScopesClicked] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [addedSource, setAddedSource] = useState(-1);
  const [fullScreenSource, setFullScreenSource] = useState<Source>();
  const [multipleEventsPopoverVisible, setMultipleEventsPopoverVisible] = useState(false);
  const [wandDisabled, setWandDisabled] = useState(false);
  const [canApplyJob, setCanApplyJob] = useState(false);
  const [applyCardOpen, setApplyCardOpen] = useState(false);
  const [timelineReady, setTimelineReady] = useState(isEditableTimeline);
  const [extractionJobStatus, setExtractionJobStatus] = useState<string | null>(null);
  const [translateDropdownOpen, setTranslateDropdownOpen] = useState(false);
  const [translateMenuMode, setTranslateMenuMode] = useState<TranslateMenuMode>('quick');
  const [quickTranslateStatus, setQuickTranslateStatus] = useState<QuickTranslateStatus>('idle');
  const [quickTranslateLanguage, setQuickTranslateLanguage] = useState<string | null>(null);
  const [quickTranslateSourceText, setQuickTranslateSourceText] = useState<string | null>(null);
  const [quickTranslateSourceLanguage, setQuickTranslateSourceLanguage] = useState<string | null>(null);
  const [quickTranslateResult, setQuickTranslateResult] = useState<string | null>(null);
  const [translateModalPos, setTranslateModalPos] = useState<{ x: number; y: number } | null>(null);
  const [isDraggingTranslateModal, setIsDraggingTranslateModal] = useState(false);
  const [answerTextLocal, setAnswerTextLocal] = useState<string | null>(null);
  const [pollForChatHistory, setPollForChatHistory] = useState<number>(0);
  const initialVisibleSources = citationsVisibleInChat(sources, mobileAnalysisEnabled) ?? sources;
  const [sourcesLocal, setSourcesLocal] = useState(initialVisibleSources);
  const [citationSources, setCitationSources] = useState<Source[] | undefined>(sources);
  // Seed from the prop the parent carried on the history row so chips paint on
  // first render for already-loaded (e.g. reloaded/restored) turns, before any
  // per-row fetch runs. applyChatHistoryItem still overwrites this from the
  // authoritative row once a live/polling fetch settles.
  const [attachmentsLocal, setAttachmentsLocal] = useState<ChatAttachment[]>(
    () => attachmentsProp ?? [],
  );
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfExportSnapshot, setPdfExportSnapshot] = useState<PdfExportSnapshot | null>(null);

  const selectionRef = useRef<Selection | null>(null);
  const rangeRef = useRef<Range | null>(null);
  const isSelectingRef = useRef(false);
  const highlightRef = useRef<SVGSVGElement | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const answerItemRef = useRef<HTMLDivElement>(null);
  const answerItemMarkdownRef = useRef<HTMLDivElement>(null);
  const pollScheduledRef = useRef(false);
  const pollAttemptRef = useRef(0);
  const prevPollEpochRef = useRef(epochTimeSecs);
  const didNotifySettledRef = useRef(false);
  /**
   * Tracks the in-flight per-item chat-history fetch so a rapid remount,
   * poll, or prop change can cancel the previous request before the next
   * one starts. Without this the older fetch could resolve after the
   * newer one and overwrite the UI with stale data.
   */
  const chatHistoryAbortRef = useRef<AbortController | null>(null);
  /** Guards async citation enrichment so stale responses cannot overwrite newer rows. */
  const citationProcessGenRef = useRef(0);
  const quickTranslateAbortRef = useRef<AbortController | null>(null);
  /** Keep the quick-translate result panel open across scroll while a result is showing. */
  const keepQuickTranslateOnScrollRef = useRef(false);
  const translateModalRef = useRef<HTMLDivElement>(null);
  const translateDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const responseCardRef = useRef<HTMLDivElement>(null);
  const sourcesCardParentRef = useRef<HTMLDivElement>(null);
  const sourcesListScrollRef = useRef<HTMLDivElement>(null);
  const preliminaryMessagesRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const [columnResizing, setColumnResizing] = useState(false);
  const leftCardWidthRef = useRef<number | null>(null);
  const rightCardWidthRef = useRef<number | null>(null);

  const statsContext = useContext(StatsContext);
  const { idToken, accessToken, realAccessToken, tenantName } = useContext(AuthContext);

  const questionId = `question_${answerIndex}`;
  const isDeepDiveQuestion =
    resultForFeedback?.request_type === 'deep_dive';
  const isMobileQuestion =
    mobileAnalysisEnabled &&
    !!resultForFeedback &&
    typeof resultForFeedback === 'object' &&
    'request_type' in resultForFeedback &&
    resultForFeedback.request_type === 'mobile';

  useLayoutEffect(() => {
    if (!pdfExportSnapshot) {
      return;
    }
    const exportRoot = answerItemMarkdownRef.current;
    if (!exportRoot) {
      setIsExportingPdf(false);
      setPdfExportSnapshot(null);
      return;
    }
    let cancelled = false;
    setIsExportingPdf(true);
    void (async () => {
      try {
        const { exportElementAsPdf } = await import('@/shared/exportPdf');
        await document.fonts?.ready;
        if (cancelled) {
          return;
        }
        const restoreTables = fitWideExportTables(exportRoot);
        try {
          await exportElementAsPdf(
            exportRoot,
            `${getCaseDisplayName(caseId)}-Answer.pdf`,
            {
              margin: [5, 5, 5, 5],
              orientation: 'portrait',
              format: 'letter',
              canvasScale: 2,
              viewport: ANSWER_PDF_VIEWPORT,
            },
          );
        } finally {
          restoreTables();
        }
      } finally {
        if (!cancelled) {
          setIsExportingPdf(false);
          setPdfExportSnapshot(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfExportSnapshot, caseId]);

  const showApplyScopes = Boolean(scopesObject && answerUsedEvidenceFilters(scopesObject));

  // needed ?
  const fixedWidthCards = {
    answer: false,
    sources: false,
  }
  const [showSources, setShowSources] = useState(!isSmallScreen);
  const [answerCardHeight, setAnswerCardHeight] = useState<number | 'auto'>('auto');
  const [sourcesCardWidth, setSourcesCardWidth] = useState<string>('auto');
  // needed ?

  const localSnapToTop = () => {
    if (isSmallScreen) {
      return;
    }
    snapToTop();
  }

  const stopPollingAnswerItem = () => {
    if (pollScheduledRef.current) {
      pollScheduledRef.current = false;
      cancelSharedChatHistoryPoll(epochTimeSecs);
    }
  };

  /**
   * True when this row is already settled for the session — bulk history
   * load, sessionStorage cache, or a prior per-item fetch marked it final.
   * Completed rows must never enter the poll loop again.
   */
  const isCompletedAnswerItem = (): boolean => {
    if (pendingPlaceholder || epochTimeSecs < 0) {
      return false;
    }
    const chatKey =
      typeof resultForFeedback?.chat_key === 'string' ? resultForFeedback.chat_key : null;
    if (isSessionAnswerSettled(chatKey, epochTimeSecs)) {
      return true;
    }
    if (resultForFeedback?.status === 'error') {
      return true;
    }
    if (
      resultForFeedback
      && !shouldPollChatHistoryItem(resultForFeedback)
    ) {
      return true;
    }
    const cachedItem = getCachedChatHistoryItem(chatKey, epochTimeSecs);
    if (cachedItem && isFinalChatHistoryItem(cachedItem)) {
      return true;
    }
    if (resultForFeedback && shouldPollChatHistoryItem(resultForFeedback)) {
      return false;
    }
    return isFinalChatHistoryItem(resultForFeedback);
  };

  const startPollingAnswerItem = (answerTextString: string) => {
    if (pendingPlaceholder || resultForFeedback?.status === 'error') {
      stopPollingAnswerItem();
      return;
    }
    if (isCompletedAnswerItem()) {
      setAnswerTextLocal(answerText);
      stopPollingAnswerItem();
      return;
    }
    const currentTimeSeconds = new Date().getTime() / 1000;
    const isOptimisticPlaceholder = epochTimeSecs < 0;
    const isRecentTurn =
      isOptimisticPlaceholder || currentTimeSeconds - epochTimeSecs < 1800;
    if (isPreliminaryMessagesText(answerTextString) && isRecentTurn) {
      stopPollingAnswerItem();
      const pollingDelay =
        pollAttemptRef.current === 0
          ? CHAT_HISTORY_FIRST_POLL_DELAY_MS
          : CHAT_HISTORY_POLL_INTERVAL_MS;
      pollAttemptRef.current += 1;
      pollScheduledRef.current = true;
      scheduleSharedChatHistoryPoll(epochTimeSecs, pollingDelay, () => {
        if (!pollScheduledRef.current) {
          return;
        }
        pollScheduledRef.current = false;
        setPollForChatHistory(new Date().getTime());
      });
    } else {
      stopPollingAnswerItem();
    }
  };

  // Clear polling timeout and cancel in-flight per-item fetch on unmount
  // so setState never lands on an unmounted component and so a long-tail
  // network request can't keep the AbortController + closure alive after
  // the answer scrolls off the list.
  useEffect(() => {
    return () => {
      stopPollingAnswerItem();
      if (chatHistoryAbortRef.current) {
        chatHistoryAbortRef.current.abort();
        chatHistoryAbortRef.current = null;
      }
    };
  }, []);

  const onDisplayedNotifiedRef = useRef(false);

  useEffect(() => {
    onDisplayedNotifiedRef.current = false;
  }, [onDisplayed]);

  useEffect(() => {
    if (!onDisplayed || onDisplayedNotifiedRef.current) {
      return;
    }
    const visibleText = (answerTextLocal ?? answerText)?.trim();
    const hasPreliminaryUi =
      !!visibleText ||
      pendingPlaceholder ||
      isPreliminaryMessagesText(answerTextLocal ?? answerText ?? '');
    if (!hasPreliminaryUi) {
      return;
    }
    onDisplayedNotifiedRef.current = true;
    const frame = requestAnimationFrame(() => {
      onDisplayed();
    });
    return () => cancelAnimationFrame(frame);
  }, [onDisplayed, answerText, answerTextLocal, pendingPlaceholder]);

  const processAndSetCitationsFromAnswer = useCallback(async (citations: Source[] | undefined) => {
    const gen = ++citationProcessGenRef.current;
    const visibleCitations = citationsVisibleInChat(citations, mobileAnalysisEnabled);
    if (!visibleCitations?.length) {
      if (gen === citationProcessGenRef.current) {
        setSourcesLocal([]);
        setCitationSources(citations);
      }
      return;
    }
    const cellId = resolveCellIdFromIdToken(idToken);
    // One POST /files/file-info for the citation list. Do not replace this
    // with Promise.all / map of getCurrentFileInfo or GET /files/file-info.
    // The map is passed through so each citation reuses the batch row.
    // Evidence-only citations and ones that already resolve from their own
    // fields are omitted because they never hit provenance.
    const lookupPaths = citationPathsForFileInfoBatch(
      visibleCitations.filter((source) => !isEvidenceOnlyCitation(source)),
      jsonMediaDisplayEnabled,
    );
    const prefetchedFileInfo = await getCurrentFileInfoBatch(
      lookupPaths,
      cellId,
      realAccessToken,
    );
    const processed = await Promise.all(
      visibleCitations.map((source) =>
        processCitationForAnswerItem(
          source,
          cellId,
          realAccessToken,
          jsonMediaDisplayEnabled,
          prefetchedFileInfo,
        ),
      ),
    );
    if (gen === citationProcessGenRef.current) {
      setCitationSources(citations);
      setSourcesLocal(processed);
    }
  }, [idToken, realAccessToken, jsonMediaDisplayEnabled, mobileAnalysisEnabled]);

  /**
   * Shared response handler used by both the cache-hit and network-hit
   * branches of {@link getAnswerFromChatHistory}. Kept inline so the
   * setState / polling side effects close over the latest component
   * state, and small enough that duplicating the parsing logic in two
   * branches would obscure the actual flow.
   */
  const applyChatHistoryItem = (item: {
    answer?: string;
    status?: string;
    attachments?: unknown;
  }) => {
    const status = ('status' in item) ? item.status : 'unknown';
    const chatKey =
      typeof resultForFeedback?.chat_key === 'string' ? resultForFeedback.chat_key : null;
    const settleTerminalRow = () => {
      stopPollingAnswerItem();
      markSessionAnswerSettled(chatKey, epochTimeSecs);
      if (!didNotifySettledRef.current) {
        didNotifySettledRef.current = true;
        onAnswerSettled?.();
      }
    };

    if (status === 'error') {
      setAttachmentsLocal([]);
      let errorText = 'An error occurred.';
      if (typeof item.answer === 'string' && item.answer.trim() !== '') {
        try {
          const parsed = JSON.parse(item.answer) as { output?: string };
          const output =
            typeof parsed.output === 'string' ? parsed.output.trim() : '';
          if (output) {
            errorText = output;
          }
        } catch {
          // keep default errorText
        }
      }
      setAnswerTextLocal(errorText);
      settleTerminalRow();
      return;
    }

    if (!item?.answer) {
      // No answer payload yet — make sure no leftover chips from a prior
      // row are showing.
      setAttachmentsLocal([]);
      if (isCompletedAnswerItem()) {
        setAnswerTextLocal(answerText);
        stopPollingAnswerItem();
        return;
      }
      setAnswerTextLocal(encodePreliminaryMessageStrings([t('chat.progress.initializing')]));
      startPollingAnswerItem(encodePreliminaryMessageStrings([t('chat.progress.initializing')]));
      return;
    }
    const answer = JSON.parse(item.answer as string);
    const outputStr = answer?.output;
    const outputIsEmpty = typeof outputStr !== 'string' || outputStr.trim() === '';
    let newAnswerText = !outputIsEmpty ? outputStr : '';
    const isInitializing = status === 'initializing';
    const isInProgress = status === 'inprogress';
    const isCompleted = status === 'completed';
    const isPrelimary = ('is_preliminary' in answer) && answer.is_preliminary === true;
    const containsMessages = !!answer && ('messages' in answer) && answer.messages.length > 0 ? true : false;
    const isPreliminaryResult = isInitializing || isInProgress || isPrelimary;

    if (isInitializing) {
      newAnswerText = encodePreliminaryMessageStrings([t('chat.progress.initializing')]);
    } else if (isPreliminaryResult && containsMessages) {
      newAnswerText = encodePreliminaryMessages(answer.messages);
    } else if (isPreliminaryResult && !outputIsEmpty) {
      newAnswerText = encodePreliminaryMessageStrings([outputStr]);
    } else if (isPreliminaryResult && outputIsEmpty) {
      newAnswerText = encodePreliminaryMessageStrings([t('chat.progress.processing')]);
    } else if (outputIsEmpty) {
      // Settled (non-initializing, non-in-progress) with no output is abnormal.
      newAnswerText = t(isCompleted
        ? 'chat.errors.answerCompleted'
        : 'chat.errors.answerGeneric');
    }
    setAnswerTextLocal(newAnswerText);

    const pollItem = {
      answer: item.answer,
      status,
    };
    if (!shouldPollChatHistoryItem(pollItem)) {
      settleTerminalRow();
    } else {
      startPollingAnswerItem(newAnswerText);
    }
    void processAndSetCitationsFromAnswer(answer.citations as Source[] | undefined);

    // Attachments live as a sibling field on the chat-history row, not inside
    // `answer` — the assistant-service writer puts them directly on the
    // DynamoDB item next to `status`, `epoch_time_secs`, etc.
    setAttachmentsLocal(parseChatAttachments(item.attachments));
  };

  const getAnswerFromChatHistory = async () => {
    if (pendingPlaceholder) {
      const placeholderText =
        answerText || encodePreliminaryMessageStrings([t('chat.progress.initializing')]);
      setAnswerTextLocal(placeholderText);
      stopPollingAnswerItem();
      return;
    }

    if (isCompletedAnswerItem()) {
      if (chatHistoryAbortRef.current) {
        chatHistoryAbortRef.current.abort();
        chatHistoryAbortRef.current = null;
      }
      stopPollingAnswerItem();
      if (answerText) {
        setAnswerTextLocal(answerText);
      }
      if (sources?.length) {
        void processAndSetCitationsFromAnswer(sources);
      }
      return;
    }

    // Cancel any prior in-flight per-item fetch BEFORE doing anything else
    // (cache lookup or new network call). Without this, a fast prop change
    // or poll cycle could leave an older response racing the newer call
    // and overwriting the UI with stale data — including the case where
    // the newer call short-circuits to a cache hit and never aborts the
    // older fetch itself.
    if (chatHistoryAbortRef.current) {
      chatHistoryAbortRef.current.abort();
    }

    // Negative epoch rows are optimistic only — Chat owns refresh until identity lands.
    if (epochTimeSecs < 0) {
      const placeholderText =
        answerText || encodePreliminaryMessageStrings([t('chat.progress.initializing')]);
      setAnswerTextLocal(placeholderText);
      stopPollingAnswerItem();
      return;
    }

    const controller = new AbortController();
    chatHistoryAbortRef.current = controller;

    // Cache lookup first: any row the bulk `loadChatHistory` fetch (or a
    // prior per-item fetch) has already memoised with a final status can
    // be served straight from sessionStorage. Only the (chat_key,
    // epoch_time_secs) pair is a stable identity for a row, so we need
    // both before we can attempt a cache hit.
    const chatKey =
      typeof resultForFeedback?.chat_key === 'string' ? resultForFeedback.chat_key : null;
    const cachedItem = getCachedChatHistoryItem<
      CacheableChatHistoryItem & { answer?: string }
    >(chatKey, epochTimeSecs);
    if (cachedItem?.answer) {
      try {
        applyChatHistoryItem(cachedItem);
        return;
      } catch {
        removeCachedChatHistoryItem(chatKey, epochTimeSecs);
      }
    }

    try {
      const cellId = resolveCellIdFromIdToken(idToken);
      if (!cellId || !realAccessToken) return;
      const history = await fetchChatHistory({
        cellId,
        accessToken: realAccessToken,
        idToken,
        signal: controller.signal,
        body: {
          userId: userId,
          caseId: caseId,
          ...caseFolderNameBody(caseFolderName),
          epochTimeSecs: epochTimeSecs,
        },
      }) as Array<CacheableChatHistoryItem & { answer?: string; status?: string }>;
      // The fetch may have resolved successfully even after a newer call
      // aborted us — abort doesn't unwind a response that already arrived.
      // Drop the result if we're no longer the active fetch so we cannot
      // overwrite the newer call's state.
      if (controller.signal.aborted) return;
      if (history[0]?.answer) {
        const currentItem = history[0];
        try {
          applyChatHistoryItem(currentItem);
          // Memoise only after a successful parse/apply so corrupt rows
          // never poison sessionStorage for every subsequent mount.
          cacheChatHistoryItem(currentItem);
        } catch {
          if (!isCompletedAnswerItem()) {
            startPollingAnswerItem(encodePreliminaryMessageStrings([t('chat.progress.initializing')]));
          } else {
            setAnswerTextLocal(answerText);
            stopPollingAnswerItem();
          }
        }
      } else if (!isCompletedAnswerItem()) {
        startPollingAnswerItem(encodePreliminaryMessageStrings([t('chat.progress.initializing')]));
      } else {
        setAnswerTextLocal(answerText);
        stopPollingAnswerItem();
      }
    } catch (error) {
      // An aborted fetch is an intentional cancellation by a newer call,
      // not a real failure — the newer call will drive any polling that
      // is still required.
      if (controller.signal.aborted) return;
      if (!isCompletedAnswerItem()) {
        startPollingAnswerItem(encodePreliminaryMessageStrings([t('chat.progress.initializing')]));
      } else {
        setAnswerTextLocal(answerText);
        stopPollingAnswerItem();
      }
    } finally {
      if (chatHistoryAbortRef.current === controller) {
        chatHistoryAbortRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (isPreliminaryMessagesText(answerTextLocal) && preliminaryMessagesRef.current) {
      if (parsePreliminaryMessages(answerTextLocal, t).length > 1) {
        preliminaryMessagesRef.current.scrollTop = preliminaryMessagesRef.current.scrollHeight - 20;
      } else {
        preliminaryMessagesRef.current.scrollTop = 0;
      }
    }
  }, [answerTextLocal]);

  // When the row identity changes (component reused for a different chat
  // turn), reset attachment chips to the new row's prop-carried set before the
  // next fetch settles so a stale `s3_key` from the prior row can never be
  // clicked. Seeding from the prop (rather than clearing to []) keeps chips
  // visible across a row swap for already-loaded turns, whose completed-row
  // fetch is short-circuited and so never repopulates via applyChatHistoryItem
  // (TR-1453).
  //
  // Adopt the prop on a genuine identity change (safety net above) OR when the
  // row is settled. While the row is still polling, applyChatHistoryItem owns
  // attachmentsLocal: a concurrent loadChatHistory remap can hand us a freshly
  // parsed empty `attachments` array (new reference), and re-seeding from it
  // would wipe chips the per-row fetch just set — mirroring how the parent-sync
  // effect below skips prop updates while shouldPollChatHistoryItem is true.
  useEffect(() => {
    const prevEpoch = prevPollEpochRef.current;
    const identityChanged = prevEpoch !== epochTimeSecs;
    if (identityChanged) {
      cancelSharedChatHistoryPoll(prevEpoch);
      pollScheduledRef.current = false;
      prevPollEpochRef.current = epochTimeSecs;
      pollAttemptRef.current = 0;
      didNotifySettledRef.current = false;
    }
    const feedback = resultForFeedback;
    const isPolling = !!feedback && shouldPollChatHistoryItem(feedback);
    if (identityChanged || !isPolling) {
      setAttachmentsLocal(attachmentsProp ?? []);
    }
  }, [epochTimeSecs, resultForFeedback, attachmentsProp]);

  // Parent (Chat) may push refreshed text onto props after its one-shot history
  // fetch — sync without a network round-trip when this row is already settled.
  useEffect(() => {
    if (pendingPlaceholder) {
      if (answerText) {
        setAnswerTextLocal(answerText);
      }
      return;
    }
    const feedback = resultForFeedback;
    if (feedback?.status === 'error') {
      if (answerText) {
        setAnswerTextLocal(answerText);
      }
      const chatKey =
        typeof feedback.chat_key === 'string' ? feedback.chat_key : null;
      markSessionAnswerSettled(chatKey, epochTimeSecs);
      stopPollingAnswerItem();
      return;
    }
    if (feedback && shouldPollChatHistoryItem(feedback)) {
      return;
    }
    if (answerText) {
      setAnswerTextLocal(answerText);
    }
    if (sources?.length) {
      void processAndSetCitationsFromAnswer(sources);
    }
    if (feedback && !shouldPollChatHistoryItem(feedback)) {
      const chatKey =
        typeof feedback.chat_key === 'string' ? feedback.chat_key : null;
      markSessionAnswerSettled(chatKey, epochTimeSecs);
      stopPollingAnswerItem();
    }
  }, [
    answerText,
    sources,
    pendingPlaceholder,
    epochTimeSecs,
    resultForFeedback,
    processAndSetCitationsFromAnswer,
  ]);

  // Re-fetch when row identity changes, not just when answer/sources
  // mutate. If the parent reuses this AnswerItem instance for a different
  // turn (e.g. virtualized list with key={index}) and the new turn happens
  // to share the same answerText/sources references, this is the only thing
  // that triggers a fresh load — without it, the row-identity reset above
  // would leave chips permanently empty.
  // `caseFolderName` resolves asynchronously from the cases list, so a cold
  // mount can fetch this row against the display-name partition and find nothing.
  // Re-run once the folder name lands; the fetch aborts its predecessor and checks the
  // row cache first, so the extra pass is cheap.
  useEffect(() => {
    getAnswerFromChatHistory();
  }, [
    pollForChatHistory,
    epochTimeSecs,
    resultForFeedback?.chat_key,
    pendingPlaceholder,
    caseFolderName,
  ]);

  useEffect(() => {
    setTimelineReady(isEditableTimeline);
  }, [isEditableTimeline]);

  useEffect(() => {
    if (extractionJob?.status === 'pending') {
      setWandDisabled(true);
      setCanApplyJob(false);
      setExtractionJobStatus('pending');
    } else if (extractionJob?.status === 'completed') {
      setWandDisabled(false);
      setCanApplyJob(true);
      setExtractionJobStatus('completed');
    } else {
      setWandDisabled(false);
      setCanApplyJob(false);
      setExtractionJobStatus(null);
    }
  }, [extractionJob, extractionJob?.status]);

  useEffect(() => {
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
    setFeedbackSubmitted(false);
    setFeedbackReason('');
  }, [ resultForFeedback, caseId, answerIndex, question, answerTextLocal, sourcesLocal ]);

  const handleFeedbackClick = (direction: 'positive' | 'negative') => {
    setFeedbackReason('');
    if (resultForFeedback?.chat_key) {
      if (direction === 'positive') {
        setPositiveFeedbackClicked(true);
        setShowFeedbackModal(true);
      } else {
        setNegativeFeedbackClicked(true);
        setShowFeedbackModal(true);
      }
      const dataForChatResultFeedback = {
        "x_org_id": featureFlags?.orgId,
        "x_short_org_id": (featureFlags?.orgId as string || '').slice(0, 8),
        "x_epoch_time_ms_start": resultForFeedback.epoch_time_ms_start,
        "x_epoch_time_ms_end": resultForFeedback.epoch_time_ms_end,
        "x_timestamp_start": resultForFeedback.epoch_time_ms_start ? new Date(resultForFeedback.epoch_time_ms_start).toISOString() : null,
        "x_timestamp_end": resultForFeedback.epoch_time_ms_end ? new Date(resultForFeedback.epoch_time_ms_end).toISOString() : null,
        "chat_key": resultForFeedback.chat_key,
        "epoch_time_secs": resultForFeedback.epoch_time_secs,
        "answer": resultForFeedback.answer,
        "case_id": resultForFeedback.case_id,
        "model_arn": resultForFeedback.model_arn,
        "question": resultForFeedback.question,
        "session_id": resultForFeedback.session_id,
        "timestamp": resultForFeedback.timestamp,
        "time_saved": resultForFeedback.time_saved,
        "user_id": resultForFeedback.user_id,
      }
      setDataForChatResultFeedback(dataForChatResultFeedback);
    }
  };

  const cancelChatResultFeedback = async () => {
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
  };

  const sendChatResultFeedback = () => {
    const extrasForFeedback = dataForChatResultFeedback;
    extrasForFeedback.feedbackReason = feedbackReason;
    const eventName = positiveFeedbackClicked? 'chat_result_positive_feedback' : 'chat_result_negative_feedback';
    const intercomEventData = {
      model_arn: dataForChatResultFeedback.model_arn,
      session_id: dataForChatResultFeedback.session_id,
      timestamp: dataForChatResultFeedback.timestamp,
      user: resultForFeedback.userId,
    };
    const inhouseEventData = {
      ...intercomEventData,
      reason: feedbackReason,
      question: dataForChatResultFeedback.question,
      output: feedbackAnswerOutput(dataForChatResultFeedback.answer),
      case_id: dataForChatResultFeedback.case_id,
      // Persist the archive turn key so a feedback row can resolve the exact
      // chat-history turn later (chat_key + epoch_time_secs is the archive PK/SK).
      chat_key: dataForChatResultFeedback.chat_key,
      epoch_time_secs: dataForChatResultFeedback.epoch_time_secs,
      request_type: resultForFeedback?.request_type,
      ...(tenantName ? { identity: { tenantName } } : {}),
    };
    // Fire-and-forget: trackEvent POSTs to /api/protected/intercom; do not block UI dismissal.
    void trackEvent(eventName, inhouseEventData);
    setPositiveFeedbackClicked(false);
    setNegativeFeedbackClicked(false);
    setShowFeedbackModal(false);
    setFeedbackSubmitted(true);
  };

  useEffect(() => {
    const refusalMessagesFromFeatureFlag: any = featureFlags['refusalMessages'];
    const passMessagesFromFeatureFlag: any = featureFlags['passMessages'];
    const llmConfigFromFeatureFlag = featureFlags['llmConfig'];
    const llmConfigRefusalMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.refusalMessages;
    const llmConfigPassMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.passMessages;

    const refusalMessagesArray = refusalMessagesFromFeatureFlag?.refusalMessages &&
      Array.isArray(refusalMessagesFromFeatureFlag.refusalMessages) ?
      refusalMessagesFromFeatureFlag.refusalMessages :
      [];

    const passMessagesArray = passMessagesFromFeatureFlag?.passMessages &&
      Array.isArray(passMessagesFromFeatureFlag.passMessages) ?
      passMessagesFromFeatureFlag.passMessages :
      [];

    const llmConfigRefusalMessagesArray = Array.isArray(llmConfigRefusalMessages) ? llmConfigRefusalMessages : [];
    const llmConfigPassMessagesArray = Array.isArray(llmConfigPassMessages) ? llmConfigPassMessages : [];

    if (llmConfigRefusalMessagesArray.length) {
      setRefusalMessages(llmConfigRefusalMessagesArray);
    } else if (refusalMessagesArray.length) {
      setRefusalMessages(refusalMessagesArray);
    } else {
      setRefusalMessages(REFUSAL_MESSAGES);
    }

    if (llmConfigPassMessagesArray.length) {
      setPassMessages(llmConfigPassMessagesArray);
    } else if (passMessagesArray.length) {
      setPassMessages(passMessagesArray);
    } else {
      setPassMessages(PASS_MESSAGES);
    }
  }, [featureFlags]);

  // Check if the answerText is a refusal
  const isRefusal = IsARefusal(answerTextLocal || '', refusalMessages, passMessages, sourcesLocal);
  const hasConcreteCase = !!caseId && caseId.trim() !== '' && caseId !== '*' && caseId !== 'All Cases';
  const shouldUseBetterWandTimelineBuild =
    !!featureFlags?.betterWand && !!featureFlags?.chatAutogenTimeline && hasConcreteCase;
  const legacyTimelineGenerationPending =
    (extractionJobStatus === 'pending' || extractionJob?.status === 'pending') && timelineReady;
  const betterWandTimelineGenerationPending =
    shouldUseBetterWandTimelineBuild && timelineGenerationStatus === 'pending';
  const betterWandTimelineGenerationCompleted =
    shouldUseBetterWandTimelineBuild && timelineGenerationStatus === 'completed';
  const timelineGenerationPending =
    legacyTimelineGenerationPending || betterWandTimelineGenerationPending;
  const timelineWandDisabled = wandDisabled || timelineGenerationPending;
  const legacyGenerateTimelineTooltip = timelineWandDisabled
    ? t('chat.answer.eventExtractionInProgress')
    : canApplyJob
      ? t('chat.answer.viewExtractedEvents')
      : t('chat.answer.generateTimelineEvents');
  const betterWandGenerateTimelineTooltip = betterWandTimelineGenerationPending
    ? t('chat.answer.timelineGenerationInProgress')
    : timelineWandDisabled
      ? t('chat.answer.eventExtractionInProgress')
      : betterWandTimelineGenerationCompleted
        ? t('chat.answer.timelineEventsGenerated')
        : canApplyJob
          ? t('chat.answer.viewExtractedEvents')
          : t('chat.answer.generateTimelineEvents');
  const generateTimelineTooltip = shouldUseBetterWandTimelineBuild
    ? betterWandGenerateTimelineTooltip
    : legacyGenerateTimelineTooltip;
  const generateTimelineLeadingIcon = timelineGenerationPending ? (
    <Spinner className="-mt-[3px] mb-[3px]" variant="wave" size="sm" color="white" />
  ) : betterWandTimelineGenerationCompleted || (canApplyJob && timelineReady) ? (
    <CheckIcon className="text-green-700" />
  ) : (
    <MagicWandIcon />
  );

  // Per-source content-moderation lookup paths. Kept as a stable-identity string
  // so the discovery effect below only re-runs when the actual paths change — not
  // every time ``sourcesLocal`` is replaced with a new array reference (e.g. when
  // citation processing swaps the raw citations for the processed list).
  const cmLookupTargets = useMemo(
    () => sourcesLocal.map((source) => resolveCmLookupPath(source) ?? null),
    [sourcesLocal],
  );
  const cmLookupKey = cmLookupTargets.map((t) => t ?? '').join('|');

  // Pre-fetch source info for all sources to discover CM categories eagerly so
  // pills and the acknowledgement gate are ready before the user first clicks.
  useEffect(() => {
    // Tenant CSAM flag + tag capability: when off we discover nothing, which
    // suppresses every downstream CM affordance (pill, acknowledgement gate,
    // and obfuscated/original switcher) since they all key off `cmDataMap`.
    if (!canSeeCsamTags) {
      setCmDataMap((prev) => (prev.size ? new Map() : prev));
      return;
    }
    // One POST /files/file-info for every CM lookup path. Do not restore
    // cmLookupTargets.forEach / map of getCurrentFileInfo. Paths already
    // loaded while resolving citation display URLs come back from the session
    // cache. bypass_cm stays a single uncached GET in the original-view effect
    // below — the batch route ignores that flag.
    const cellId = resolveCellIdFromIdToken(idToken);
    if (!cellId || !realAccessToken) return;
    // Clear stale index-keyed entries before re-discovering. `cmDataMap` is keyed
    // by citation index, so when the citation list is reprocessed or reordered
    // (``cmLookupKey`` changes) an old slot can still point at a now-clean source.
    // The population loop below only *sets* entries when a CM type is found, so
    // without this reset a CSAM pill/acknowledgement would stay attached to the
    // wrong source until a slow fetch happened to overwrite that exact slot.
    setCmDataMap((prev) => (prev.size ? new Map() : prev));
    let cancelled = false;
    const targets = cmLookupTargets.filter((target): target is string => !!target);
    void (async () => {
      try {
        const infos = await getCurrentFileInfoBatch(targets, cellId, realAccessToken);
        if (cancelled) return;
        setCmDataMap((prev) => {
          const next = new Map(prev);
          let changed = false;
          cmLookupTargets.forEach((lookupTarget, index) => {
            if (!lookupTarget) return;
            const info = infos.get(lookupTarget);
            const cm = info?.contentModeration;
            const cmType = cm?.type && cm.findingStatus !== 'REVIEWED' ? cm.type : undefined;
            if (!cmType) return;
            next.set(index, {
              type: cmType,
              obfUrl: info?.previewUrl ?? null,
              origUrl: undefined,
            });
            changed = true;
          });
          return changed ? next : prev;
        });
      } catch {
        // ignore fetch errors — CM features degrade gracefully
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmLookupKey, idToken, realAccessToken, canSeeCsamTags]);

  // When the user switches to the "Original" view for a CM-flagged source, fetch the
  // bypass URL on-demand. This records the audit log at the moment the user actually
  // requests to see the unobfuscated content, so it MUST hit the server every time —
  // hence the uncached ``getFileInfoFromServer`` (not the sessionStorage-cached
  // ``getCurrentFileInfo``) plus an ``origUrl`` reset when switching back to
  // Obfuscated so the next switch to Original re-issues the ``bypass_cm=true`` request.
  useEffect(() => {
    if (selectedSourceIndex === null) return;
    const cmEntry = cmDataMap.get(selectedSourceIndex);
    if (!cmEntry?.type) return;

    if (effectiveShowObfuscated) {
      // Back on the safe view (or a non-reviewer pinned to it) — clear any fetched
      // original so returning to Original triggers a fresh, audited bypass request.
      if (cmEntry.origUrl !== undefined) {
        setCmDataMap((prev) => {
          const next = new Map(prev);
          const existing = next.get(selectedSourceIndex);
          if (existing) next.set(selectedSourceIndex, { ...existing, origUrl: undefined });
          return next;
        });
      }
      return;
    }

    if (cmEntry.origUrl !== undefined) return; // already fetched or in flight for this view

    const selectedSource = sourcesLocal[selectedSourceIndex];
    const filePath = selectedSource ? resolveCmLookupPath(selectedSource) : undefined;
    const cellId = resolveCellIdFromIdToken(idToken);
    if (!filePath || !cellId || !realAccessToken) return;

    // Ignore a late response if the user switched back to Obfuscated (or moved to
    // another source) before this request resolved. Without this, a stale ``.then``
    // would set ``origUrl`` while on the safe view, and the next switch to Original
    // would reuse that cached URL and skip the required fresh, audited bypass request.
    let cancelled = false;

    getFileInfoFromServer(filePath, cellId, realAccessToken, true)
      .then((info) => {
        if (cancelled) return;
        // Files that never needed conversion have no ``preview_url`` even with
        // ``bypass_cm`` — their original lives at ``file_url``. Fall back to it so
        // "Original" renders the real image instead of silently staying obfuscated.
        const originalUrl = info?.previewUrl ?? info?.fileUrl ?? null;
        setCmDataMap((prev) => {
          const next = new Map(prev);
          const existing = next.get(selectedSourceIndex);
          if (existing) next.set(selectedSourceIndex, { ...existing, origUrl: originalUrl });
          return next;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setCmDataMap((prev) => {
          const next = new Map(prev);
          const existing = next.get(selectedSourceIndex);
          if (existing) next.set(selectedSourceIndex, { ...existing, origUrl: null });
          return next;
        });
      });

    return () => {
      cancelled = true;
    };
  // cmDataMap in deps is intentional: re-check origUrl each time cmDataMap updates.
  // The `cmEntry.origUrl !== undefined` guard prevents re-fetching within one view.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSourceIndex, effectiveShowObfuscated, cmDataMap]);

  const openSources = (sourceIndex: number) => {
    const cmEntry = cmDataMap.get(sourceIndex);
    const cmType = cmEntry?.type;
    if (cmType) {
      setContentModerationAckIndex(sourceIndex);
    } else {
      setSelectedSourceIndex(sourceIndex);
      setSourcesMediaOpen(true);
      setShowObfuscatedPreview(true);
    }
  };

  const ensureSourceListItemVisible = useCallback((sourceIndex: number) => {
    const attemptScroll = (retriesLeft: number) => {
      const scrollContainer = sourcesListScrollRef.current;
      if (!scrollContainer) {
        if (retriesLeft > 0) {
          requestAnimationFrame(() => attemptScroll(retriesLeft - 1));
        }
        return;
      }

      const listItem = scrollContainer.querySelector<HTMLElement>(
        `[data-source-list-index="${sourceIndex}"]`,
      );
      if (!listItem) {
        if (retriesLeft > 0) {
          requestAnimationFrame(() => attemptScroll(retriesLeft - 1));
        }
        return;
      }

      listItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    requestAnimationFrame(() => attemptScroll(5));
  }, []);

  const openSourcesFromCitation = (sourceIndex: number) => {
    openSources(sourceIndex);
    ensureSourceListItemVisible(sourceIndex);
  };

  const closeSources = () => {
    setSourcesMediaOpen(false);
    setSelectedSourceIndex(null);
    setShowObfuscatedPreview(true);
    // Reset the width of responseCardRef
    setLeftCardWidth(null); // Reset to default or initial width
  };

  const handleCopyClick = async (question: string, answerIndex: number, sources: Source[], answerTextCopy: string) => {
    const copied = await handleCopyToClipboard(question, answerIndex, sources, answerTextCopy, t);
    setCopied(copied);
  };

  const handleAddSourceToScope = async (source: Source, index: number) => {
    const path = pathForAssistantScope(source);
    if (path) {
      addSourceToScope(path);
      setAddedSource(index);
    }
  };

  useEffect(() => {
    if (addedSource !== -1) {
      setTimeout(() => {
        setAddedSource(-1);
      }, 1500);
    }
  }, [addedSource]);

  const handleCopySourceForTimeline = async (source: Source, index: number) => {
    try {
      const pathToCopy = extractPathAfterBulk(source.sourceFilePath || source.truncatedFilePath || '').truncatedFilePath;
      source.sourceContext = buildTimelineCopySourceContext(source);
      const sourceToCopy = {
        source,
        caseId: caseId,
      }
      if (pathToCopy) {
        await navigator.clipboard.writeText(pathToCopy);
        sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
        setCopiedSource(index);
      }
    } catch (error) {
      console.error('Failed to copy source path:', error);
    }
  };

  useEffect(() => {
    if (copiedSource !== -1) {
      setTimeout(() => {
        setCopiedSource(-1);
      }, 1500);
    }
  }, [copiedSource]);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 1500);
    }
  }, [copied]);

  const handleDownload = async (source: Source) => {
    const cellId = resolveCellIdFromIdToken(idToken);
    const session = cellId && realAccessToken ? { cellId, accessToken: realAccessToken } : undefined;
    try {
      const url = await getDownloadUrl(source, session);
      window.open(url.signedUrl, '_blank');
    } catch (error) {
      const errorMessage = `Error downloading document: ${error}`;
      CaptureError(errorMessage, error as Error);
      alert(errorMessage);
    }
  };

  const handleAttachmentDownload = async (attachment: ChatAttachment) => {
    try {
      const cellId = resolveCellIdFromIdToken(idToken);
      if (!cellId || !realAccessToken) {
        // Without these we can't call the sign endpoint. Silently bailing
        // would leave the chip clickable-but-dead with no feedback; surface
        // the same error path other attachment failures use.
        throw new Error('Cannot download attachment: missing session');
      }
      if (isMarkdownAttachment(attachment)) {
        window.open(markdownAttachmentDownloadUrl(attachment), '_blank');
        return;
      }
      const response = await signChatAttachment({
        cellId,
        accessToken: realAccessToken,
        idToken,
        s3Key: attachment.s3_key,
      });
      const signedUrl = response?.signed_url;
      if (typeof signedUrl !== 'string' || signedUrl.trim() === '') {
        // A 200 OK with a missing/empty signed_url would otherwise open a
        // blank tab and leave the user wondering why nothing happened.
        throw new Error('signed_url missing in sign response');
      }
      window.open(sanitizeHttpUrlForBrowserLoad(signedUrl), '_blank');
    } catch (error) {
      const errorMessage = `Error downloading attachment: ${error}`;
      CaptureError(errorMessage, error as Error);
      alert(errorMessage);
    }
  };

  const handleRedoClick = () => {
    setIsExpanded?.(true);
    setRedoClicked(true);
    onRedo(question);
    setTimeout(() => {
      setRedoClicked(false);
    }, 1500);
  };

  const handleApplyScopesClick = () => {
    setIsExpanded?.(true);
    setApplyScopesClicked(true);
    setCurrentScopesAndFiles(scopesObject);
    setTimeout(() => {
      setApplyScopesClicked(false);
    }, 1500);
  };

  useEffect(() => {
    setShowSources(!isSmallScreen);
  }, [isSmallScreen]);

  useEffect(() => {
    if (responseCardRef.current && !isSmallScreen) {
      setAnswerCardHeight(responseCardRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (!isSmallScreen && sourcesMediaOpen) {
      setSourcesCardWidth('600px');
    } else {
      setSourcesCardWidth('fit-content');
      setRightCardWidth(null);
    }
  }, [sourcesMediaOpen, isSmallScreen]);

  const handleMouseDown = (event: React.MouseEvent) => {
    leftCardWidthRef.current = responseCardRef.current?.offsetWidth || 0;
    rightCardWidthRef.current = sourcesCardParentRef.current?.offsetWidth || 0;
    event.preventDefault(); // Prevent default text selection behavior
    isDragging.current = true;
    setColumnResizing(true);
  };

  const handleMouseUp = () => {
    setColumnResizing(false);
    if (isDragging.current && !isSmallScreen) {
      isDragging.current = false;
    }
  };

  const handleMouseMove = useCallback((event: MouseEvent) => {
    event.preventDefault();
    if (isDragging.current && !isSmallScreen) {
      const delta = event.movementX;

      // Define minimum widths for the cards
      const minLeftWidth = 300; // Minimum width for the left card
      // Conditionally set the minimum width for the right card
      const mediaType =
        sourcesMediaOpen && selectedSourceIndex !== null
          ? getMediaType(sourcesLocal[selectedSourceIndex], sourcesLocal[selectedSourceIndex]?.fileInfo)
          : 'unknown';
      const isAudioOrVideo = mediaType === 'audio' || mediaType === 'video';
      const minRightWidth = isAudioOrVideo
        ? 730 // Minimum width when mediaDisplay is present
        : 565; // Adjust this value as needed for non-media content

      // Calculate new widths
      leftCardWidthRef.current = fixedWidthCards.answer
        ? leftCardWidthRef.current || 0
        : Math.max(minLeftWidth, (leftCardWidthRef.current || responseCardRef.current?.offsetWidth || 0) + delta || 0);

      rightCardWidthRef.current = fixedWidthCards.sources
        ? rightCardWidthRef.current || 0
        : Math.max(minRightWidth, (rightCardWidthRef.current || sourcesCardParentRef.current?.offsetWidth || 0) - delta || 0);

      // Margin on both sides of the viewport
      const margin = 40;
      // Calculate total width and ensure it doesn't exceed the viewport width
      const totalWidth = leftCardWidthRef.current + rightCardWidthRef.current + 10;
      const maxWidth = window.innerWidth - margin;
      if (totalWidth <= maxWidth) {
        if (!fixedWidthCards.answer) {
          setLeftCardWidth(leftCardWidthRef.current);
        }
        if (!fixedWidthCards.sources && sourcesMediaOpen) {
          setRightCardWidth(rightCardWidthRef.current);
        }
      } else {
        // Adjust widths to fit within the viewport
        const excessWidth = totalWidth - maxWidth;
        if (!fixedWidthCards.answer) {
          setLeftCardWidth(leftCardWidthRef.current - excessWidth / 2);
        }
        if (!fixedWidthCards.sources && sourcesMediaOpen) {
          setRightCardWidth(rightCardWidthRef.current - excessWidth / 2);
        }
      }
    }
  }, [fixedWidthCards, sourcesMediaOpen, sourcesLocal, selectedSourceIndex]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [ handleMouseMove, handleMouseUp ]);

  const restoreSelection = useCallback(() => {
    if (rangeRef.current) {
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(rangeRef.current);
      }
    }
  }, []);

  const resetTranslateMenu = useCallback(() => {
    quickTranslateAbortRef.current?.abort();
    quickTranslateAbortRef.current = null;
    keepQuickTranslateOnScrollRef.current = false;
    translateDragRef.current = null;
    setIsDraggingTranslateModal(false);
    setTranslateDropdownOpen(false);
    setTranslateMenuMode('quick');
    setQuickTranslateStatus('idle');
    setQuickTranslateLanguage(null);
    setQuickTranslateSourceText(null);
    setQuickTranslateSourceLanguage(null);
    setQuickTranslateResult(null);
    setTranslateModalPos(null);
  }, []);

  const openTranslateModal = useCallback(() => {
    setTranslateMenuMode('quick');
    setQuickTranslateStatus('idle');
    setQuickTranslateLanguage(null);
    setQuickTranslateSourceText(null);
    setQuickTranslateSourceLanguage(null);
    setQuickTranslateResult(null);
    keepQuickTranslateOnScrollRef.current = true;
    const anchorX = contextMenu?.x ?? Math.round(window.innerWidth / 2 - TRANSLATE_MODAL_LANG_SIZE.width / 2);
    const anchorY = (contextMenu?.y ?? Math.round(window.innerHeight / 3)) + 40;
    setTranslateModalPos(
      clampTranslateModalPosition(
        anchorX,
        anchorY,
        TRANSLATE_MODAL_LANG_SIZE.width,
        TRANSLATE_MODAL_LANG_SIZE.height,
      ),
    );
    setTranslateDropdownOpen(true);
  }, [contextMenu]);

  const reclampTranslateModal = useCallback((mode: TranslateMenuMode = translateMenuMode) => {
    setTranslateModalPos((prev) => {
      if (!prev) return prev;
      const size = mode === 'result' ? TRANSLATE_MODAL_RESULT_SIZE : TRANSLATE_MODAL_LANG_SIZE;
      const el = translateModalRef.current;
      const width = el?.offsetWidth || size.width;
      const height = el?.offsetHeight || size.height;
      return clampTranslateModalPosition(prev.x, prev.y, width, height);
    });
  }, [translateMenuMode]);

  const handleTranslateModalDragStart = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!translateModalPos || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    translateDragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: translateModalPos.x,
      originY: translateModalPos.y,
    };
    setIsDraggingTranslateModal(true);
  }, [translateModalPos]);

  const handleTranslateModalDragMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const drag = translateDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    const size = translateMenuMode === 'result' ? TRANSLATE_MODAL_RESULT_SIZE : TRANSLATE_MODAL_LANG_SIZE;
    const el = translateModalRef.current;
    const width = el?.offsetWidth || size.width;
    const height = el?.offsetHeight || size.height;
    setTranslateModalPos(
      clampTranslateModalPosition(
        drag.originX + (e.clientX - drag.startX),
        drag.originY + (e.clientY - drag.startY),
        width,
        height,
      ),
    );
  }, [translateMenuMode]);

  const handleTranslateModalDragEnd = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const drag = translateDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      // ignore if already released
    }
    translateDragRef.current = null;
    setIsDraggingTranslateModal(false);
  }, []);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatchCopyToHistory(text);
      if (rangeRef.current) {
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(rangeRef.current);
        }
      }
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
    if (highlightRef.current) {
      highlightRef.current.remove();
      highlightRef.current = null;
    }
    setContextMenu(null);
    resetTranslateMenu();
  };

  const handleSelection = (selection: Selection) => {
    if (selection.toString().length > 0) {
      // Store the selection and range
      selectionRef.current = selection;
      const range = selection.getRangeAt(0);
      rangeRef.current = range;

      // Create highlight element
      if (highlightRef.current) {
        highlightRef.current.remove();
      }

      // Create SVG element
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('svg-highlight-answer-item');
      svg.style.position = 'fixed';
      svg.style.top = '0';
      svg.style.left = '0';
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '1';

      // Create path for the highlight
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('fill', 'rgba(84, 126, 173, 0.3)');

      // Create path data from all selection ranges
      let pathData = '';
      let firstRect: DOMRect | null = null;
      let lastRect: DOMRect | null = null;
      let lastLineRect: DOMRect | null = null;
      let lastLineElement: HTMLElement | null = null;

      // Only process the first range to avoid expanding selection
      const rects = Array.from(range.getClientRects()) as DOMRect[];

      rects.forEach((rect, index) => {
        const x = rect.left;
        const y = rect.top;
        const width = rect.width;
        const height = rect.height;

        if (!firstRect) {
          firstRect = rect;
        }
        lastRect = rect;

        // Keep track of the last line's rectangle and element
        if (!lastLineRect || rect.top > lastLineRect.top) {
          lastLineRect = rect;
          // Find the containing line element
          const node = range.commonAncestorContainer;
          const element = node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement;
          if (element) {
            // Find the closest block-level element or list item
            let lineElement = element;
            while (lineElement &&
                  !lineElement.classList.contains('text-white') &&
                  !lineElement.classList.contains('font-light') &&
                  !lineElement.classList.contains('text-[14px]') &&
                  !lineElement.classList.contains('leading-snug') &&
                  !lineElement.tagName.toLowerCase().match(/^(p|li|div)$/)) {
              lineElement = lineElement.parentElement as HTMLElement;
            }
            if (lineElement) {
              lastLineElement = lineElement;
            }
          }
        }

        if (index === 0) {
          pathData += `M ${x} ${y} h ${width} v ${height} h -${width} Z`;
        } else {
          pathData += ` M ${x} ${y} h ${width} v ${height} h -${width} Z`;
        }
      });

      path.setAttribute('d', pathData);
      svg.appendChild(path);
      document.body.appendChild(svg);
      highlightRef.current = svg;

      // Only set context menu if we have a valid last line element and rectangle
      if (lastLineElement && lastLineRect) {
        const rect = lastLineRect as DOMRect;
        setContextMenu({
          x: rect.left + window.scrollX,
          y: rect.bottom + window.scrollY + 10
        });
        setSelectedText(selection.toString());
      }
    }
  };

  useEffect(() => {
    if (!answerItemRef.current) {
      return;
    }

    const handleSelectDown = (e: MouseEvent) => {
      const targetElement = e.target as HTMLElement;
      if (targetElement.closest('.CONTEXT_MENU_REF_CLASS')) {
        return;
      }
      isSelectingRef.current = true;
    };

    const handleSelectUp = (e: MouseEvent) => {
      const targetElement = (e.target as any);
      const isInContextMenu = targetElement.closest('.CONTEXT_MENU_REF_CLASS');
      if (isInContextMenu) {
        return;
      }

      isSelectingRef.current = false;

      const selection = window.getSelection();
      if (selection) {
        // Check if this was a triple click
        const clickCount = e.detail;
        if (clickCount === 3) {
          const target = e.target as HTMLElement;

          // Find the closest text block element
          let blockElement = target;
          while (blockElement &&
                 !blockElement.classList.contains('text-white') &&
                 !blockElement.classList.contains('font-light') &&
                 !blockElement.classList.contains('text-[14px]') &&
                 !blockElement.classList.contains('leading-snug')) {
            blockElement = blockElement.parentElement as HTMLElement;
          }

          if (blockElement) {
            const range = document.createRange();
            range.selectNodeContents(blockElement);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        handleSelection(selection);
      }
    };

    const handleSelectClick = (e: MouseEvent) => {
      const contextMenuElement = contextMenuRef.current;
      if (contextMenuElement && !contextMenuElement.contains(e.target as Node)) {
        // Drag-to-select ends with mouseup then a click on the same text; don't dismiss the
        // custom context menu that mouseup just opened.
        if (
          answerItemRef.current?.contains(e.target as Node) &&
          window.getSelection()?.toString()?.trim()
        ) {
          return;
        }
        setContextMenu(null);
        // Floating translate modal stays open until dismissed via its own controls.
        if (!keepQuickTranslateOnScrollRef.current) {
          quickTranslateAbortRef.current?.abort();
          quickTranslateAbortRef.current = null;
          setTranslateDropdownOpen(false);
          setTranslateMenuMode('quick');
          setQuickTranslateStatus('idle');
          setQuickTranslateLanguage(null);
          setQuickTranslateSourceText(null);
          setQuickTranslateSourceLanguage(null);
          setQuickTranslateResult(null);
          setTranslateModalPos(null);
        }
        if (highlightRef.current) {
          highlightRef.current.remove();
          highlightRef.current = null;
        }
      }
    };

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && !isSelectingRef.current) {
        handleSelection(selection);
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(`#${questionId}`)) {
        e.preventDefault();
        const selection = window.getSelection();
        if (selection) {
          handleSelection(selection);
        }
      }
    };

    const handleSelctScroll = (e: Event) => {
      // Only hide context menu if scrolling outside of the context menu itself
      const target = e.target as HTMLElement;
      const contextMenuElement = contextMenuRef.current;

      // Check if the scroll event is coming from within the context menu or its dropdown
      if (contextMenuElement && (
          contextMenuElement.contains(target) ||
          target.closest('.translate-dropdown-container') ||
          target.closest('.quick-translate-modal') ||
          target.closest('.custom-context-menu')
        )) {
        return; // Don't hide context menu if scrolling within it
      }

      // Selection highlight is a fixed SVG from viewport rects — always clear it
      // on scroll so it does not float over the wrong content.
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      // Keep the floating translate modal open across scroll (dismiss via X instead).
      // Still clear the selection toolbar — the modal is independent now.
      if (keepQuickTranslateOnScrollRef.current) {
        setContextMenu(null);
        return;
      }
      setContextMenu(null);
      quickTranslateAbortRef.current?.abort();
      quickTranslateAbortRef.current = null;
      setTranslateDropdownOpen(false);
      setTranslateMenuMode('quick');
      setQuickTranslateStatus('idle');
      setQuickTranslateLanguage(null);
      setQuickTranslateSourceText(null);
      setQuickTranslateSourceLanguage(null);
      setQuickTranslateResult(null);
      setTranslateModalPos(null);
    };

    if (answerItemRef.current.getAttribute('listenersAttacthed') !== 'true') {
      answerItemRef.current.setAttribute('listenersAttacthed', 'true');
      answerItemRef.current.addEventListener('mousedown', handleSelectDown);
      answerItemRef.current.addEventListener('mouseup', handleSelectUp);
      answerItemRef.current.addEventListener('click', handleSelectClick);
      answerItemRef.current.addEventListener('selectionchange', handleSelectionChange);
      answerItemRef.current.addEventListener('contextmenu', handleContextMenu);
    }
    window.addEventListener('scroll', handleSelctScroll, true); // Use capture phase to catch all scroll events

    return () => {
      if (answerItemRef.current) {
        answerItemRef.current.setAttribute('listenersAttacthed', 'false');
        answerItemRef.current.removeEventListener('mousedown', handleSelectDown);
        answerItemRef.current.removeEventListener('mouseup', handleSelectUp);
        answerItemRef.current.removeEventListener('click', handleSelectClick);
        answerItemRef.current.removeEventListener('selectionchange', handleSelectionChange);
        answerItemRef.current.removeEventListener('contextmenu', handleContextMenu);
      }
      window.removeEventListener('scroll', handleSelctScroll, true);
      if (highlightRef.current) {
        highlightRef.current.remove();
      }
    };
  }, [questionId]);

  const handleSendToQuery = async () => {
    if (selectedText && onSendToQuery) {
      setIsExpanded?.(true);
      // Wait for the expansion animation to complete
      await new Promise(resolve => setTimeout(resolve, 750));
      onSendToQuery(`Tell me more about: ${selectedText}`);
      setContextMenu(null);
      resetTranslateMenu();
      // Focus the textarea after a short delay to ensure it's ready
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      setContextMenu(null);
    }
  };

  const handleTranslateToLanguage = async (
    language: string,
    /** Prefer stored quick-translate source over live selection (Open in chat). */
    textOverride?: string | null,
  ) => {
    const textToSend = (textOverride ?? selectedText ?? '').trim();
    if (textToSend && onSendToQuery) {
      setIsExpanded?.(true);
      // Wait for the expansion animation to complete
      await new Promise(resolve => setTimeout(resolve, 750));
      onSendToQuery(`Translate the following text to ${language}: ${textToSend}`);
      resetTranslateMenu();
      setContextMenu(null);
      // Focus the textarea after a short delay to ensure it's ready
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 100);
      if (highlightRef.current) {
        highlightRef.current.remove();
        highlightRef.current = null;
      }
      setContextMenu(null);
    }
  };

  const handleQuickTranslateToLanguage = async (language: string) => {
    const textToTranslate = sanitizeQuickTranslateText(selectedText ?? '');
    if (!textToTranslate) return;

    quickTranslateAbortRef.current?.abort();
    const controller = new AbortController();
    quickTranslateAbortRef.current = controller;

    setTranslateMenuMode('result');
    setQuickTranslateStatus('loading');
    setQuickTranslateLanguage(language);
    setQuickTranslateSourceText(textToTranslate);
    setQuickTranslateSourceLanguage(null);
    setQuickTranslateResult(null);
    keepQuickTranslateOnScrollRef.current = true;

    try {
      const cellId = resolveCellIdFromIdToken(idToken);
      if (!cellId || !realAccessToken) {
        throw new Error('Missing cell session for translation.');
      }

      const idTokenInfo = getTokenInfo(idToken || '');
      const accessTokenInfo = getTokenInfo(realAccessToken);
      const requestUserId =
        userId || getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
      if (!requestUserId) {
        throw new Error('Missing user identity for translation.');
      }

      // Mirror the in-chat translate payload (same scopes / case / tenant). Prefer
      // enrichEvent so the turn stays out of the chat answer list; empty scopes
      // previously produced “We encountered an issue processing your query…”.
      const files = (scopesObject?.files ?? []).map(toSearchablePath).filter(Boolean);
      const scopes = (scopesObject?.scopes ?? []).map(toSearchablePath).filter(Boolean);
      const excludedFiles = (scopesObject?.excludedFiles ?? [])
        .map(toSearchablePath)
        .filter(Boolean);
      const excludedScopes = (scopesObject?.excludedScopes ?? [])
        .map(toSearchablePath)
        .filter(Boolean);
      const contentCategories = [
        ...new Set(
          (scopesObject?.contentCategories ?? [])
            .filter((c): c is string => typeof c === 'string' && c.length > 0)
            .map((c) => c.toLowerCase()),
        ),
      ];

      const caseApiName = getCaseInternalName(caseId).trim();
      const displayUser = String(idTokenInfo?.name ?? '').trim();
      const tenant =
        accessTokenInfo?.['custom:tenant'] ||
        idTokenInfo?.['custom:tenant'] ||
        tenantName ||
        null;

      const buildBody = (): Record<string, unknown> => {
        // Panel-only enrich turn: LANGUAGE: prefix so the UI can label the source language.
        // Keep sessionId null so this never lands in the user's chat answer list.
        const query =
          `Translate the following text to ${language}. ` +
          `Reply with only the detected source language name in English uppercase, a colon, a space, ` +
          `and the translation — for example: SPANISH: <translated text>. ` +
          `Do not include any other commentary, labels, or markup.\n\n` +
          `${textToTranslate}`;
        const body: Record<string, unknown> = {
          enrichEvent: true,
          query,
          deepDive: false,
          threatReport: false,
          selectedCase: caseId,
          ...caseFolderNameBody(caseFolderName),
          files,
          scopes,
          excludedFiles,
          excludedScopes,
          userId: requestUserId,
          sessionId: null,
          regularRagAnswerVerificationEnabled: !!featureFlags?.regularRagAnswerVerification,
          deepDiveAnswerVerificationEnabled: !!featureFlags?.deepDiveAnswerVerification,
          answerVerificationDebugOutputEnabled: !!featureFlags?.answerVerificationDebugOutput,
          ...(featureFlags?.contentCategories && contentCategories.length > 0
            ? { contentCategories }
            : {}),
        };
        if (caseApiName && caseApiName !== '*') {
          body.caseName = caseApiName;
        }
        if (displayUser) {
          body.userName = displayUser;
        }
        if (tenant) {
          body.tenant = tenant;
        }
        return body;
      };

      const toPollItem = (
        row: Record<string, unknown>,
        /** POST often omits status; chat treats that as still in progress. */
        assumeInProgressIfMissing = false,
      ): {
        answer?: string;
        status?: string;
      } => ({
        answer: typeof row?.answer === 'string' ? row.answer : undefined,
        status:
          typeof row?.status === 'string'
            ? row.status
            : assumeInProgressIfMissing
              ? 'inprogress'
              : undefined,
      });

      const isAssistantProcessingFailure = (text: string) =>
        /we encountered an issue processing your query/i.test(text);

      const runAndPoll = async () => {
        let result = await postAssistantChat({
          cellId,
          accessToken: realAccessToken,
          idToken,
          body: buildBody(),
          signal: controller.signal,
        });
        if (controller.signal.aborted) {
          return null;
        }

        const identity = parsePendingTurnIdentityFromAssistantResponse(result);
        if (!identity) {
          throw new Error('Assistant did not return a translation turn identity.');
        }

        const historyBody: Record<string, unknown> = {
          enrichEvent: result?.enrich_event ?? true,
          sessionId: result?.sessionId ?? null,
          epochTimeSecs: identity.epochTimeSecs,
          chatKey: identity.chatKey ?? result?.chat_key,
          requestType: result?.request_type,
        };

        // Match chat.tsx: missing POST status ⇒ inprogress so the poll loop enters.
        let latestItem = toPollItem(result, true);
        const startTime = Date.now();
        while (
          (isEnrichmentHistoryPending(latestItem.status) ||
            shouldPollChatHistoryItem(latestItem)) &&
          Date.now() - startTime < ENRICHMENT_POLL_TIMEOUT_MS
        ) {
          await new Promise((resolve) => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
          if (controller.signal.aborted) {
            return null;
          }

          const history = await fetchChatHistory({
            cellId,
            accessToken: realAccessToken,
            idToken,
            signal: controller.signal,
            body: historyBody,
          });
          if (controller.signal.aborted) {
            return null;
          }

          result = history as Record<string, unknown>;
          latestItem = toPollItem(result);
        }

        return latestItem;
      };

      // enrichEvent only — never auto-retry as a normal chat turn (that would
      // insert a translate Q&A into the user's history). Failures offer Open in chat.
      const latestItem = await runAndPoll();
      if (controller.signal.aborted || !latestItem) return;

      const translated = extractAssistantOutputText(latestItem, t('chat.answer.translationFailed'));

      if (
        !translated ||
        latestItem.status === 'error' ||
        isAssistantProcessingFailure(translated)
      ) {
        setQuickTranslateStatus('error');
        setQuickTranslateResult(
          isAssistantProcessingFailure(translated)
            ? t('chat.answer.translationFailedRetry')
            : translated || t('chat.answer.translationFailed'),
        );
        return;
      }
      if (
        isEnrichmentHistoryPending(latestItem.status) ||
        shouldPollChatHistoryItem(latestItem)
      ) {
        setQuickTranslateStatus('error');
        setQuickTranslateResult(t('chat.answer.translationTimedOut'));
        return;
      }

      const { detectedLanguage, translation } = parseQuickTranslatePrefixedResult(translated);
      setQuickTranslateSourceLanguage(detectedLanguage);
      setQuickTranslateResult(translation);
      setQuickTranslateStatus('done');
    } catch (error) {
      if (controller.signal.aborted) return;
      CaptureError(
        `Quick translate failed: ${(error as Error)?.message || 'unknown error'}`,
        error as Error,
      );
      setQuickTranslateStatus('error');
      setQuickTranslateResult(t('chat.answer.translationFailedRetry'));
    } finally {
      if (quickTranslateAbortRef.current === controller) {
        quickTranslateAbortRef.current = null;
      }
    }
  };

  // Close language picker when clicking outside; keep result/loading modal until X/Back.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!translateDropdownOpen) return;
      const target = event.target as HTMLElement;
      if (target.closest('.quick-translate-modal') || target.closest('.translate-dropdown-container')) {
        return;
      }
      // Result/loading modal is intentional — dismiss only via its controls.
      if (translateMenuMode === 'result') return;
      resetTranslateMenu();
    };

    if (translateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [translateDropdownOpen, translateMenuMode, resetTranslateMenu]);

  // Keep the floating modal fully on-screen after open / mode changes (result is taller).
  useLayoutEffect(() => {
    if (!translateDropdownOpen || !translateModalPos) return;
    reclampTranslateModal(translateMenuMode);
  }, [translateDropdownOpen, translateMenuMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!translateDropdownOpen) return;
    const onResize = () => reclampTranslateModal();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [translateDropdownOpen, reclampTranslateModal]);

  useEffect(() => {
    return () => {
      quickTranslateAbortRef.current?.abort();
    };
  }, []);

  // Sources array with CM-aware modifications applied for the currently selected source.
  // Points previewFilePath at the variant for the active view and sets bypassCm so the
  // file-info SourceCard fetches for itself describes the same variant (the server
  // applies obfuscation or bypass on that request).
  const sourcesForCard = useMemo(() => {
    if (selectedSourceIndex === null) return sourcesLocal;
    const cmData = cmDataMap.get(selectedSourceIndex);
  // The obfuscated/original preview override is gated on `canSeeCsamTags`.
  // Original-image reveal still requires `canRevealOriginal`.
    if (!cmData?.type || !canSeeCsamTags) return sourcesLocal;
    return sourcesLocal.map((s, i) => {
      if (i !== selectedSourceIndex) return s;
      const label = s.label ?? s.sourceFilePath?.split('/').pop() ?? undefined;
      // Resolve the preview URL for the active view:
      //   Obfuscated → the pre-fetched blurred asset (cmData.obfUrl).
      //   Original   → the audited bypass URL (cmData.origUrl). Stay on the blurred
      //                asset while that fetch is in flight (undefined) so the reveal
      //                happens once, from one URL — the s3 proxy audits every fetch
      //                of the original, so a placeholder that also resolves to it
      //                would double-log the view. A `null`/empty `origUrl` means that
      //                fetch failed or returned no URL, so it stays on the blurred
      //                asset too: switching would blank the preview (no URL to render)
      //                and `bypassCm` below would make SourceCard's own file-info
      //                request re-issue the audited bypass.
      const originalUrl = cmData.origUrl || undefined;
      const showOriginal = !effectiveShowObfuscated && originalUrl !== undefined;
      const previewForView = showOriginal ? originalUrl : (cmData.obfUrl ?? undefined);
      // Strip every dispatched-path field so the CM preview (obfuscated OR audited
      // original) always wins. For container citations (e.g. an image extracted
      // from a zip) the source carries a ``dispatchedFilePath`` pointing at the
      // raw extract; resolvePreferredMediaDisplayForSource uses "dispatched-first"
      // ordering for AV extracts and would otherwise return that raw path —
      // ignoring the CM preview entirely. That both breaks the obfuscated view
      // (leaks unobfuscated CSAM / blank tile) and the original view (returns an
      // unrenderable extract such as a .heic instead of its .webp conversion).
      const overridden = {
        ...s,
        label,
        previewFilePath: previewForView,
        // SourceCard always re-fetches provenance file-info for a chat citation, and
        // ``resolvePreferredMediaDisplayForSource`` prefers that record's ``previewUrl``
        // over ``previewFilePath`` whenever the cited object itself types as image / AV.
        // Without this flag that record is the CM-safe one, so its obfuscated
        // ``previewUrl`` outranks the original above and the Original view keeps
        // rendering the blurred asset (non-container image citations never switched).
        // ``effectiveShowObfuscated`` already pins non-reviewers to the safe view, so
        // this can only request the bypass for a csam-reviewer.
        bypassCm: showOriginal,
        dispatchedFilePath: undefined,
        sourceContext: s.sourceContext
          ? { ...s.sourceContext, dispatchedFilePath: undefined, dispatched_file_path: undefined }
          : s.sourceContext,
      } as MediaSource & { dispatched_file_path?: string };
      overridden.dispatched_file_path = undefined;
      return overridden;
    });
  }, [sourcesLocal, selectedSourceIndex, cmDataMap, effectiveShowObfuscated, canSeeCsamTags]);

  useEffect(() => {
    if (selectedSourceIndex !== undefined && selectedSourceIndex !== null) {
      setFullScreenSource(sourcesForCard[selectedSourceIndex]);
    }
  }, [selectedSourceIndex, sourcesForCard]);

  // When changing the selected source, sync fullscreen media viewer's page to the selected source's page.
  // Use functional setter and omit fullscreenMediaItem from deps so multiple AnswerItems don't oscillate
  // (each would otherwise see the other's write as a mismatch and overwrite, causing "Maximum update depth exceeded").
  useEffect(() => {
    if (selectedSourceIndex === null || selectedSourceIndex === undefined || !sourcesLocal[selectedSourceIndex]) return;
    const selectedSource = sourcesLocal[selectedSourceIndex];
    const selectedMediaUrl = resolvePreferredMediaDisplayForSource(
      selectedSource,
      selectedSource.fileInfo,
    ).mediaUrl || selectedSource.sourceFilePath;
    const pageNum = selectedSource.page != null
      ? (typeof selectedSource.page === 'number' ? selectedSource.page : parseInt(String(selectedSource.page), 10))
      : 1;
    if (Number.isNaN(pageNum)) return;
    setFullscreenMediaItem((prev: MediaSource | null): MediaSource | null => {
      if (!prev) return prev;
      const fullscreenUrl = prev.mediaUrl ?? prev.originalUrl;
      if (!fullscreenUrl || fullscreenUrl !== selectedMediaUrl) return prev;
      if (prev.mediaContext?.pageNumber === pageNum) return prev;
      return { ...prev, mediaContext: { ...prev.mediaContext, pageNumber: pageNum } };
    });
  }, [selectedSourceIndex, sourcesLocal, setFullscreenMediaItem]);

  useEffect(() => {
    if (answerItemRef.current) {
      const mimicCopy = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && (event.key === 'c' || event.key === 'C')) {
          const active = document.activeElement;
          if (
            active instanceof HTMLInputElement ||
            active instanceof HTMLTextAreaElement ||
            (active instanceof HTMLElement && active.isContentEditable)
          ) {
            return;
          }
          handleCopy(selectedText);
        }
      }

      document.addEventListener('keydown', mimicCopy);

      return () => {
        document.removeEventListener('keydown', mimicCopy);
      };
    }
  }, [selectedText]);

  return (
    <div
      id={questionId}
      ref={answerItemRef}
      data-answer-item-scrollport
      className={`z-1 box-border flex flex-col select-text custom-scrollbar2 lg:flex-row
        flex-grow justify-center bg-white/5 lg:bg-white/0 border-1 border-solid border-white/5 rounded-md p-0 lg:p-3
        ${fillHeight
          ? `h-full max-h-full min-h-0 max-w-full m-0 ${isSmallScreen ? 'overflow-y-auto' : 'overflow-hidden'}`
          : `max-w-[90%] lg:max-w-auto m-4 overflow-y-auto ${isSmallScreen ? '' : 'h-[calc(100vh-200px)] max-h-[calc(100vh-200px)]'}`}
        ${sourcesMediaOpen ? 'gap-2' : 'gap-3'}
      `}
      style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const selection = window.getSelection();
        if (selection) {
          handleSelection(selection);
        }
      }}
    >
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="CONTEXT_MENU_REF_CLASS fixed bg-[#2C3A4A] rounded p-1 z-[1000] shadow-lg flex flex-row gap-2 custom-context-menu"
          style={{
            left: contextMenu.x,
            top: contextMenu.y
          }}
          onMouseDown={(e) => { e.stopPropagation(); return false; }}
          onMouseUp={(e) => { e.stopPropagation(); return false; }}
          onClick={(e) => {
            e.stopPropagation();
            restoreSelection();
          }}
        >
          <TpTooltip content={t('chat.answer.copy')} delayDuration={500} sideOffset={6}>
            <Button
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('chat.answer.copy')}
              onMouseDown={(e) => { e.stopPropagation(); return false; }}
              onMouseUp={(e) => { e.stopPropagation(); return false; }}
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(selectedText);
              }}
            >
              <CopyIcon alt={t('chat.answer.copy')} />
            </Button>
          </TpTooltip>
          <div className="relative translate-dropdown-container">
            <TpTooltip content={t('chat.answer.translateInto')} delayDuration={500} sideOffset={6}>
              <Button
                iconOnly
                size="sm"
                style="text"
                color="light"
                background="inverse"
                aria-label={t('chat.answer.translateInto')}
                onMouseDown={(e) => { e.stopPropagation(); return false; }}
                onMouseUp={(e) => { e.stopPropagation(); return false; }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (translateDropdownOpen) {
                    resetTranslateMenu();
                  } else {
                    openTranslateModal();
                  }
                }}
              >
                <TranslateIcon alt={t('chat.answer.translate')} />
              </Button>
            </TpTooltip>
          </div>

          <TpTooltip content={t('chat.answer.tellMeMore')} delayDuration={500} sideOffset={6}>
            <Button
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('chat.answer.tellMeMore')}
              onMouseDown={(e) => { e.stopPropagation(); return false; }}
              onMouseUp={(e) => { e.stopPropagation(); return false; }}
              onClick={(e) => {
                e.stopPropagation();
                handleSendToQuery();
              }}
            >
              <MagnifyingGlassIcon alt={t('chat.answer.tellMeMore')} />
            </Button>
          </TpTooltip>
        </div>
      )}

      {translateDropdownOpen && translateModalPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={translateModalRef}
          className={`quick-translate-modal box-border fixed z-[1100] flex flex-col overflow-hidden bg-[#2C3A4A] rounded-md shadow-lg border border-white/10 ${
              translateMenuMode === 'result'
                ? 'min-w-[280px] max-w-[360px] max-h-[min(360px,calc(100vh-16px))]'
                : 'min-w-[160px] max-h-[min(240px,calc(100vh-16px))]'
            } ${isDraggingTranslateModal ? 'select-none' : ''}`}
          style={{
            left: translateModalPos.x,
            top: translateModalPos.y,
          }}
          onMouseDown={(e) => { e.stopPropagation(); }}
          onMouseUp={(e) => { e.stopPropagation(); }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="box-border shrink-0 flex items-center gap-1 w-full px-2 py-1.5 text-left text-white/80 text-xs bg-[#2C3A4A] border-b border-white/10 cursor-grab active:cursor-grabbing"
            onPointerDown={handleTranslateModalDragStart}
            onPointerMove={handleTranslateModalDragMove}
            onPointerUp={handleTranslateModalDragEnd}
            onPointerCancel={handleTranslateModalDragEnd}
          >
            <DotsSixIcon size={14} weight="bold" className="shrink-0 text-white/50" aria-hidden />
            <span className="flex-1 truncate">
              {translateMenuMode === 'result'
                ? (quickTranslateLanguage
                    ? t('chat.answer.translateTo', { language: quickTranslateLanguage })
                    : t('chat.answer.translation'))
                : translateMenuMode === 'quick'
                  ? t('chat.answer.quickTranslate')
                  : t('chat.answer.translateInChat')}
            </span>
            <Button
              type="button"
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('chat.answer.closeTranslation')}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                resetTranslateMenu();
                setContextMenu(null);
                if (highlightRef.current) {
                  highlightRef.current.remove();
                  highlightRef.current = null;
                }
              }}
              className="h-5 w-5 shrink-0 rounded-sm border-none bg-transparent text-white/60 hover:bg-white/15"
            >
              <XIcon weight="bold" />
            </Button>
          </div>

          {(translateMenuMode === 'quick' || translateMenuMode === 'chat') && (
            <>
              <div className="box-border min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto custom-scrollbar2">
                {languageOptions.map((language) => (
                  <TpTooltip
                    key={language.code}
                    content={language.englishName}
                    delayDuration={1000}
                    sideOffset={6}
                    placement="left"
                  >
                    <Button
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onMouseDown={(e) => { e.stopPropagation(); return false; }}
                      onMouseUp={(e) => { e.stopPropagation(); return false; }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (translateMenuMode === 'quick') {
                          void handleQuickTranslateToLanguage(language.name);
                        } else {
                          void handleTranslateToLanguage(language.name);
                        }
                      }}
                      className="box-border w-full px-2 py-1 text-left text-white text-sm hover:bg-white/10 hover:cursor-pointer"
                    >
                      {language.name}
                    </Button>
                  </TpTooltip>
                ))}
              </div>
              {translateMenuMode === 'quick' ? (
                <Button
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  onMouseDown={(e) => { e.stopPropagation(); return false; }}
                  onMouseUp={(e) => { e.stopPropagation(); return false; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTranslateMenuMode('chat');
                  }}
                  className="box-border shrink-0 w-full px-2 py-1.5 text-left text-white/70 text-xs bg-[#2C3A4A] border-t border-white/10 hover:bg-white/10 hover:text-white hover:cursor-pointer rounded-b-md"
                >
                  Translate in chat…
                </Button>
              ) : (
                <Button
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  onMouseDown={(e) => { e.stopPropagation(); return false; }}
                  onMouseUp={(e) => { e.stopPropagation(); return false; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTranslateMenuMode('quick');
                  }}
                  className="box-border shrink-0 w-full px-2 py-1.5 text-left text-white/70 text-xs bg-[#2C3A4A] border-t border-white/10 hover:bg-white/10 hover:text-white hover:cursor-pointer rounded-b-md"
                >
                  ← {t('chat.answer.quickTranslate')}
                </Button>
              )}
            </>
          )}

          {translateMenuMode === 'result' && (
            <div className="relative box-border min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto custom-scrollbar2 flex flex-col gap-2 p-2">
              {quickTranslateSourceText && (
                <div className="box-border flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-white/50">
                    {quickTranslateSourceLanguage || t('chat.answer.originalText')}
                  </span>
                  <div className="box-border max-h-[90px] overflow-y-auto custom-scrollbar2 rounded border border-white/10 bg-black/10 px-2 py-1.5 text-sm text-white/80 whitespace-pre-wrap break-words">
                    {quickTranslateSourceText}
                  </div>
                </div>
              )}

              <div className="box-border flex flex-col gap-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-white/50">
                    {quickTranslateLanguage || t('chat.answer.translation')}
                  </span>
                  {quickTranslateStatus === 'loading' && (
                    <Spinner size="sm" color="white" />
                  )}
                </div>
                {quickTranslateStatus === 'loading' && (
                  <p className="m-0 text-sm text-white/80">{t('chat.answer.translating')}</p>
                )}
                {(quickTranslateStatus === 'done' || quickTranslateStatus === 'error') && (
                  <div
                    className={`box-border max-h-[120px] overflow-y-auto custom-scrollbar2 rounded border border-white/10 bg-black/20 px-2 py-1.5 text-sm whitespace-pre-wrap break-words ${
                      quickTranslateStatus === 'error' ? 'text-red-200' : 'text-white'
                    }`}
                  >
                    {quickTranslateResult}
                  </div>
                )}
              </div>

              {(quickTranslateStatus === 'done' || quickTranslateStatus === 'error') && (
                <div className="flex flex-row items-center gap-1">
                  {quickTranslateStatus === 'done' && quickTranslateResult && (
                    <Button
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onMouseDown={(e) => { e.stopPropagation(); return false; }}
                      onMouseUp={(e) => { e.stopPropagation(); return false; }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigator.clipboard.writeText(quickTranslateResult).then(() => {
                          dispatchCopyToHistory(quickTranslateResult);
                        }).catch(() => {});
                      }}
                      className="flex items-center gap-1 px-1.5 py-1 border-none rounded-sm bg-transparent text-white/70 hover:text-white hover:bg-white/10 hover:cursor-pointer text-xs"
                      leadingIcon={<CopyIcon weight="light" />}
                    >
                      {t('chat.answer.copy')}
                    </Button>
                  )}
                  {quickTranslateStatus === 'error' && quickTranslateLanguage && (
                    <Button
                      size="sm"
                      style="text"
                      color="light"
                      background="inverse"
                      onMouseDown={(e) => { e.stopPropagation(); return false; }}
                      onMouseUp={(e) => { e.stopPropagation(); return false; }}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleTranslateToLanguage(
                          quickTranslateLanguage,
                          quickTranslateSourceText,
                        );
                      }}
                      className="px-1.5 py-1 border-none rounded-sm bg-transparent text-white/70 hover:text-white hover:bg-white/10 hover:cursor-pointer text-xs"
                    >
                      {t('chat.answer.openInChat')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    onMouseDown={(e) => { e.stopPropagation(); return false; }}
                    onMouseUp={(e) => { e.stopPropagation(); return false; }}
                    onClick={(e) => {
                      e.stopPropagation();
                      quickTranslateAbortRef.current?.abort();
                      quickTranslateAbortRef.current = null;
                      keepQuickTranslateOnScrollRef.current = true;
                      setQuickTranslateStatus('idle');
                      setQuickTranslateResult(null);
                      setQuickTranslateLanguage(null);
                      setQuickTranslateSourceText(null);
                      setQuickTranslateSourceLanguage(null);
                      setTranslateMenuMode('quick');
                    }}
                    className="ml-auto px-1.5 py-1 border-none rounded-sm bg-transparent text-white/70 hover:text-white hover:bg-white/10 hover:cursor-pointer text-xs"
                  >
                    {t('chat.answer.back')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}

      <HotKeys
        onCloseModal={closeSources}
        // While app fullscreen is up, Escape must only exit fullscreen (main.tsx /
        // MediaDisplay) — not close the SourceCard the user returns to.
        isModal={sourcesMediaOpen && !isFullscreen}
      />
      <Card
        ref={responseCardRef}
        style={{
          width: leftCardWidth || '33%',
          flexGrow: leftCardWidth ? 0 : 1,
          minWidth: isSmallScreen ? '100%' : (sourcesMediaOpen ? '300px' : '300px'),
          maxWidth: isSmallScreen ? '80%' : '100%',
          cursor: fixedWidthCards.answer ? 'default' : 'auto',
          height: fillHeight && !isSmallScreen ? '100%' : 'auto',
          alignSelf: fillHeight && !isSmallScreen ? 'stretch' : 'flex-start',
          overflowX: 'hidden',
        }}
        classNames={{
          base: `${fillHeight ? 'h-full max-h-full min-h-0' : 'h-[calc(100vh-227px)] max-h-[calc(100vh-227px)]'} box-border flex bg-transparent shadow-lg border-[1px] border-white/5 border-solid bg-black/10 rounded-md m-0 p-0`,
          header: 'box-border flex flex-row w-full items-start m-0 px-2 lg:px-4 rounded-none border-b-[1px] border-solid border-white/5 bg-white/5',
          body: 'box-border flex flex-col items-start m-0 p-2 lg:p-4 bg-black/15 selectable-text',
          footer: 'box-border min-h-[45px] flex flex-row gap-2 items-center m-0 rounded-none px-2 py-2 m-0 bg-white/5 border-t-[1px] border-solid border-white/5 ',
        }}
      >
        <CardHeader
          data-question-bar-interactive
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex w-full min-w-0 items-start gap-2">
            <div className="block min-w-0 flex-1 text-blue-100">
              <h3
                className="max-h-[90px] overflow-y-auto custom-scrollbar2 m-0 p-0 font-light text-xl lg:text-2xl italic leading-tight"
              >
                <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-1">
                  <span>{question}</span>
                  {isDeepDiveQuestion && (
                    <TpTooltip
                      content={t('chat.composer.deepDiveHelp')}
                      delayDuration={500}
                      sideOffset={6}
                    >
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-solid border-white/50 px-2 py-0.5 text-xs font-medium not-italic text-[var(--text-inverse)]"
                        aria-label={t('chat.answer.deepDiveQuestion')}
                      >
                        <CrosshairIcon size={16} weight="regular" aria-hidden />
                        <span>{t('chat.composer.deepDive')}</span>
                      </span>
                    </TpTooltip>
                  )}
                  {isMobileQuestion && (
                    <TpTooltip content={t('chat.answer.mobileQuestion')} delayDuration={500} sideOffset={6}>
                      <span className="inline-flex align-middle ml-[3px] text-blue-400">
                        <DeviceMobileIcon size={16} weight="duotone" />
                      </span>
                    </TpTooltip>
                  )}
                </span>
              </h3>
            </div>
            {!readOnly && (
              <div className="flex shrink-0 flex-col items-start">
                <Button
                  type="button"
                  size="sm"
                  style={redoClicked ? "solid" : "text"}
                  color={redoClicked ? "accent" : "light"}
                  background="inverse"
                  data-testid="RedoQuestion"
                  aria-label={t(redoClicked
                    ? 'chat.answer.questionAdded'
                    : 'chat.answer.askAgain')}
                  leadingIcon={
                    redoClicked ? (
                      <CheckIcon weight="bold" />
                    ) : (
                      <ArrowClockwiseIcon weight="bold" />
                    )
                  }
                  className="shrink-0"
                  onClick={(event) => {
                    handleRedoClick();
                    recordStat(
                      'RedoQuestionClicked',
                      event,
                      statsContext,
                    );
                  }}
                >
                  {t(redoClicked ? 'chat.answer.added' : 'chat.answer.askAgain')}
                </Button>
                {showApplyScopes && (
                  <Button
                    type="button"
                    size="sm"
                    style={applyScopesClicked ? "solid" : "text"}
                    color={applyScopesClicked ? "accent" : "light"}
                    background="inverse"
                    data-testid="ApplyScopes"
                    aria-label={
                      applyScopesClicked
                        ? t('chat.answer.scopesAdded')
                        : t('chat.answer.reuseScopeHelp')
                    }
                    leadingIcon={
                      applyScopesClicked ? (
                        <CheckIcon weight="bold" />
                      ) : (
                        <FunnelIcon weight="bold" />
                      )
                    }
                    className="shrink-0"
                    onClick={(event) => {
                      handleApplyScopesClick();
                      recordStat(
                        'ApplyScopesClicked',
                        event,
                        statsContext,
                      );
                    }}
                  >
                    {t(applyScopesClicked ? 'chat.answer.added' : 'chat.answer.reuseScope')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <div className='overflow-y-auto custom-scrollbar2 w-[calc(100%-1px)] h-[100%]' onScroll={localSnapToTop}>
            {isRefusal ? (
              <div className='flex flex-col lg:flex-row items-start gap-2 p-4'>
                <div className='text-[14px] lg:text-lg'>
                  {answerTextLocal}
                </div>
                <Popover>
                  <TpTooltip content={t('chat.answer.learnMore')} delayDuration={500} sideOffset={6}>
                    <PopoverTrigger>
                      <Button
                        type="button"
                        iconOnly
                        size="sm"
                        style="text"
                        color="light"
                        background="inverse"
                        aria-label={t('chat.answer.responseInformation')}
                        aria-haspopup="dialog"
                        className="mt-[-20px] ml-[5px] lg:mt-0 lg:ml-0"
                      >
                        <InfoIcon weight="duotone" />
                      </Button>
                    </PopoverTrigger>
                  </TpTooltip>
                  <PopoverContent className={INVERSE_OVERLAY_SURFACE_CLASS}>
                    <div className='flex flex-row m-0 pb-2 gap-2 align-top border-b-[.5px] border-[hsla(0,0%,100%,0.9)] border-solid'>
                      <InfoIcon size={40} weight="light" />
                      <h3 className='m-0 p-0 italic font-normal'>&ldquo;{t('chat.answer.refusalQuote')}&rdquo;</h3>
                    </div>
                    <p>{t('chat.answer.refusalExplanation')}</p>
                    <p>{t('chat.answer.refusalFeedback')}</p>
                    <p>{t('chat.answer.refusalRewording')}</p>
                    <ol>
                      <li>{t('chat.answer.refusalTipPossible')}</li>
                      <li>{t('chat.answer.refusalTipSynonyms')}</li>
                      <li>{t('chat.answer.refusalTipPastTense')}</li>
                    </ol>
                    <p>{t('chat.answer.generalSuggestions')}</p>
                    <ol>
                      <li>{t('chat.answer.generalTipSpecific')}</li>
                      <li>{t('chat.answer.generalTipContext')}</li>
                      <li>{t('chat.answer.generalTipFollowUp')}</li>
                      <li>{t('chat.answer.generalTipRephrase')}</li>
                    </ol>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <div id={`answerText_${answerIndex}`} className="min-h-full">
                <div className="min-h-full">
                  {(() => {
                    const displayAnswerText = answerTextLocal ?? answerText;
                    const showPreliminary =
                      !displayAnswerText
                      || displayAnswerText.trim() === ''
                      || isPreliminaryMessagesText(displayAnswerText);
                    return showPreliminary ? (
                    <div className="flex flex-row w-[calc(100%-10px)] h-full min-h-[100px] ml-[4px]">
                      <div
                        onScroll={localSnapToTop}
                        ref={preliminaryMessagesRef}
                        className="w-[calc(100%-10px)] flex flex-col gap-0 text-white font-light text-[14px] leading-snug m-0 overflow-y-auto custom-scrollbar2"
                        aria-live="polite"
                        role="status"
                      >
                        {(() => {
                          const preliminaryMessages = parsePreliminaryMessages(displayAnswerText, t);
                          const lastIndex = preliminaryMessages.length - 1;
                          return preliminaryMessages.map((message: string, index: number) => (
                            <div
                              key={index}
                              className="box-border w-full h-fit flex flex-row gap-0 items-center text-white font-light text-[14px] leading-snug m-0 min-h-fit"
                            >
                              <div className="min-w-[31px] flex flex-row items-center justify-start p-0">
                                {index === lastIndex ? (
                                  <div className="relative z-1 flex flex-row gap-2 items-start justify-start w-full min-h-[32px]">
                                    <div className="w-[27px] h-[27px] p-[3px] box-border flex items-center justify-center bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                                      <Spinner variant="spinner" size="sm" className="[&_i]:bg-blue-300" />
                                    </div>
                                    <div className="box-border flex font-light w-full text-[14px] leading-snug min-h-[32px] mt-[4px] text-blue-300 whitespace-pre-line">
                                      {message.trim() === "Analysis complete."
                                        ? t('chat.progress.analysisComplete')
                                        : message}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="relative z-1 flex flex-row-reverse gap-2 items-start justify-start w-full min-h-[21px]">
                                    <div className="relative z-1 box-border flex font-light w-full text-[14px] leading-snug pb-[10px] min-h-[32px] text-white whitespace-pre-line pl-[20px] -ml-[19px] mt-[2px] border-l-[1px] border-white border-solid">
                                      {message}
                                    </div>
                                    <div className="relative z-2 box-border flex items-center justify-start w-[21px] h-[21px] max-h-[21px] min-h-[21px] p-[3px] ml-[3px] bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                                      <CheckIcon size={15} weight="bold" className="relative z-3 text-green-500" />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  ) : (
                    <ReactMarkdown
                      components={{
                        ...answerTableComponents,
                        h1: ({ node, ...props }) => <h1 className="text-blue-400 font-medium text-lg lg:text-2xl border-b-[2px] border-solid border-blue-200 m-0 p-0 mt-1 mb-3 pb-0.5" {...props} />,
                        h2: ({ node, ...props }) => <h2 className="text-blue-300 font-normal text-base lg:text-base m-0 p-0 pt-1 pb-2" {...props} />,
                        h3: ({ node, ...props }) => <h3 className="text-blue-200 font-medium text-[15px] m-0 p-0 mt-3 mb-1 first:mt-0" {...props} />,
                        ul: ({ node, ...props }) => <StyledList {...props} />,
                        ol: ({ node, ...props }) => <StyledList ordered {...props} />,
                        li: ({ node, ...props }) => <StyledListItem {...props} />,
                        a: ({ node, children }) => <span>{children}</span>,
                        p: ({ node, children }) => {
                          // Check if children is empty or only contains whitespace
                          const isEmpty = !children || (Array.isArray(children) && children.every(child =>
                            typeof child === 'string' && child.trim() === ''
                          ));
                          return isEmpty ? (
                            <p className="hidden" /> // Empty paragraph
                          ) : (
                            <p className="text-white font-light text-[14px] leading-snug m-0 pb-2 lg:max-w-[800px]">
                              {children}
                            </p>
                          );
                        },
                        sup: ({ node, children }) => {
                          const originalIndex = getSourceIndexFromCitationSupChildren(children);
                          const sourceIndex =
                            originalIndex == null
                              ? null
                              : visibleIndexForCitationSuperscript(
                                  citationSources,
                                  mobileAnalysisEnabled,
                                  originalIndex + 1,
                                );
                          const isClickable =
                            sourceIndex !== null &&
                            sourceIndex >= 0 &&
                            sourceIndex < sourcesLocal.length;

                          const handleCitationClick = (event?: React.MouseEvent) => {
                            if (!isClickable || sourceIndex === null) return;
                            openSourcesFromCitation(sourceIndex);
                            recordStat('OpenSourcesClicked', event, statsContext);
                          };

                          return (
                            <sup
                              className={`${CITATION_SUP_CLASSNAME}${isClickable ? ' cursor-pointer hover:bg-brand-blue' : ''}`}
                              style={CITATION_SUP_STYLE}
                              onClick={isClickable ? handleCitationClick : undefined}
                              onKeyDown={isClickable ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleCitationClick();
                                }
                              } : undefined}
                              role={isClickable ? 'button' : undefined}
                              tabIndex={isClickable ? 0 : undefined}
                            >
                              {children}
                            </sup>
                          );
                        },
                      }}
                      rehypePlugins={markdownRehypeRawSanitizePlugins}
                      remarkPlugins={[remarkGfm]}
                    >
                      {normalizePresentationMarkdown(displayAnswerText ?? '')}
                    </ReactMarkdown>
                  );
                  })()}
                </div>
              </div>
            )}
          </div>
          <div className="absolute bottom-[1px] right-[1px] w-[1px] h-[1px] bg-black overflow-hidden">
            <div ref={answerItemMarkdownRef} className="flex w-full min-w-0 flex-row items-start justify-start">
              {pdfExportSnapshot && (
                <div className="flex w-full min-w-0 flex-col items-start justify-start gap-1 text-black">
                  <div className="w-full mb-3 pb-1">
                    <p className="text-blue-600 font-semibold text-[12px] uppercase tracking-wider m-0 pb-1">{t('chat.answer.question')}</p>
                    <p className="text-black font-medium text-[14px] leading-snug m-0">{pdfExportSnapshot.question}</p>
                  </div>
                  <ReactMarkdown
                    components={{
                      ...answerExportTableComponents,
                      h1: ({ node, ...props }) => <h1 className="text-blue-600 font-semibold text-lg border-b-[2px] border-solid border-blue-200 m-0 p-0 mt-1 mb-3 pb-0.5" {...props} />,
                      h2: ({ node, ...props }) => <h2 className="text-blue-500 font-semibold text-base m-0 p-0 pt-1 pb-2" {...props} />,
                      h3: ({ node, ...props }) => <h3 className="text-blue-400 font-medium text-[15px] m-0 p-0 mt-3 mb-1 first:mt-0" {...props} />,
                      ul: ({ node, ...props }) => <StyledList className="text-black" {...props} />,
                      ol: ({ node, ...props }) => <StyledList ordered className="text-black" {...props} />,
                      li: ({ node, ...props }) => <StyledListItem className="text-black" {...props} />,
                      a: ({ node, children }) => <span className="text-black">{children}</span>,
                      p: ({ node, children }) => {
                        // Check if children is empty or only contains whitespace
                        const isEmpty = !children || (Array.isArray(children) && children.every(child =>
                          typeof child === 'string' && child.trim() === ''
                        ));
                        return isEmpty ? (
                          <p className="hidden" /> // Empty paragraph
                        ) : (
                          <p className="text-black font-light text-[14px] leading-snug m-0 pb-2">
                            {children}
                          </p>
                        );
                      },
                      sup: ({ node, children }) => (
                        <sup
                          className={CITATION_SUP_CLASSNAME_LIGHT}
                          style={CITATION_SUP_STYLE_LIGHT}
                        >
                          {children}
                        </sup>
                      ),
                    }}
                    rehypePlugins={markdownRehypeRawSanitizePlugins}
                    remarkPlugins={[remarkGfm]}
                  >
                    {normalizePresentationMarkdown(pdfExportSnapshot.answerText)}
                  </ReactMarkdown>
                  {pdfExportSnapshot.sources.length > 0 && (
                    <div className="flex flex-col items-start justify-start gap-2">
                      <div className="text-blue-400 font-semibold text-[14px] leading-snug m-0 mt-2 pb-2">{t('chat.answer.sources')}:</div>
                      {pdfExportSnapshot.sources.map((source, index) => {
                        const sourceMediaType = sourcesMediaOpen
                          ? getMediaType(source, source.fileInfo)
                          : null;
                        const { showPageNumber, timeSuffix, pageLabel, pageValue } = sourceListLabelParts(source, sourceMediaType, t);
                        let sourceText = `${getFilename(source)}${showPageNumber ? `, ${pageLabel} ${pageValue}` : ''}${timeSuffix}`;
                        if (source.sourceFilePath?.endsWith('.mbox') || source.sourceFilePath?.endsWith('.mbx')) {
                          if (!!source.analysisFilePath && source.analysisFilePath !== source.sourceFilePath) {
                            const attachmentFileName =  source.analysisFilePath.split('/').pop();
                            if (!!attachmentFileName && attachmentFileName.trim() !== '') {
                              sourceText += `, ${attachmentFileName}`;
                            }
                          }
                        }
                        const dispatchedPath = getDispatchedFilePath(source);
                        const dispatchedItemFileName =
                          dispatchedPath && dispatchedPath !== source.sourceFilePath
                            ? dispatchedPath.split('?')[0].split('/').pop() || ''
                            : '';
                        return (
                          <div
                            key={source.index}
                            className="flex flex-row items-start justify-start gap-2"
                          >
                            <div className="w-[21px] h-[21px] min-w-[21px] min-h-[21px] box-border bg-[#dddddd] text-black rounded-full text-[10px] flex items-center justify-center">{index + 1}</div>
                            <div className="flex flex-col items-start justify-start">
                              <div className={`break-all text-[14px] text-black ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>{sourceText}</div>
                              {dispatchedItemFileName && (
                                <div className={`break-all text-[12px] text-black ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                                  {t('chat.answer.item')}: {dispatchedItemFileName}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardBody>
        {(featureFlags.chatAttachments || onAttachmentDownload) && attachmentsLocal.length > 0 && (
          <AttachmentList attachments={attachmentsLocal} onDownload={onAttachmentDownload ?? handleAttachmentDownload} />
        )}
        {!isRefusal && (
          <CardFooter className="flex flex-row justify-between">
            <div className="flex flex-row gap-1">
              {featureFlags?.exportAnswerAsPdf && (
                <TpTooltip content={t('chat.answer.downloadPdf')} delayDuration={500} sideOffset={6}>
                  <Button
                    iconOnly
                    size="sm"
                    style="text"
                    color="light"
                    background="inverse"
                    data-testid="DownloadPDFOfResponse"
                    aria-label={t('chat.answer.downloadPdf')}
                    onClick={(event) => {
                      const exportText = answerTextLocal || '';
                      if (
                        isExportingPdf
                        || !exportText.trim()
                        || isPreliminaryMessagesText(exportText)
                      ) {
                        return;
                      }
                      setIsExportingPdf(true);
                      setPdfExportSnapshot({
                        question,
                        answerText: exportText,
                        sources: sourcesLocal,
                      });
                      recordStat(
                        'DownloadPDFOfResponseClicked',
                        event,
                        statsContext                        );
                    }}
                  >
                    {isExportingPdf ? <Spinner className="-mt-[3px] mb-[3px]" variant="wave" size="sm" color="white" /> : <FilePdfIcon />}
                  </Button>
                </TpTooltip>
              )}
              <TpTooltip content={t(copied ? 'chat.answer.copied' : 'chat.answer.copyResponse')} delayDuration={500} sideOffset={6}>
                <Button
                  iconOnly
                  size="sm"
                  style={copied ? "solid" : "text"}
                  color={copied ? "accent" : "light"}
                  background="inverse"
                  data-testid="CopyResponse"
                  aria-label={t(copied ? 'chat.answer.copied' : 'chat.answer.copyResponse')}
                  className="id-CopyResponse"
                  onClick={(event) => {
                    handleCopyClick(question, answerIndex, sourcesLocal, answerTextLocal || '');
                    recordStat(
                      'CopyResponseClicked',
                      event,
                      statsContext
                    );
                  }}
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </Button>
              </TpTooltip>
              {!readOnly && (
              <>
              <TpTooltip content={t('chat.answer.createTimelineEvent')} delayDuration={500} sideOffset={6}>
                <Button
                  iconOnly
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  data-testid="CreateTimelineEventFromResponse"
                  aria-label={t('chat.answer.createTimelineEvent')}
                  onClick={(event) => {
                    addToTimeline({
                      answerText: answerTextLocal,
                      sources: sourcesLocal,
                      media: buildEventMediaFromSources(sourcesLocal, jsonMediaDisplayEnabled),
                      autogen: false,
                      epochTimeSecs: epochTimeSecs,
                    } as EventFromChat);
                    recordStat(
                      'CreateTimelineEventFromResponseClicked',
                      event,
                      statsContext                    );
                  }}
                >
                  <ListPlusIcon />
                </Button>
              </TpTooltip>
              <TpTooltip
                content={generateTimelineTooltip}
                delayDuration={500}
                sideOffset={6}
              >
                <Button
                  size="sm"
                  style="text"
                  color="light"
                  background="inverse"
                  data-testid="GenerateTimelineEvents"
                  disabled={timelineWandDisabled}
                  aria-label={generateTimelineTooltip}
                  leadingIcon={generateTimelineLeadingIcon}
                  onClick={(event) => {
                    if (timelineWandDisabled) {
                      return;
                    }
                    if (canApplyJob && (extractionJobStatus === 'completed' || extractionJob?.status === 'completed')) {
                      setApplyCardOpen(true);
                    } else if (shouldUseBetterWandTimelineBuild) {
                      void generateTimelineFromAnswer({
                        question,
                        answerText: answerTextLocal || '',
                        sources: sourcesLocal,
                        epochTimeSecs,
                      });
                    } else {
                      setExtractionJobStatus('pending');
                      addToTimeline({
                        answerText: answerTextLocal,
                        sources: sourcesLocal,
                        media: buildEventMediaFromSources(sourcesLocal, jsonMediaDisplayEnabled),
                        autogen: true,
                        epochTimeSecs: epochTimeSecs,
                      } as EventFromChat);
                    }
                    recordStat(
                      'GenerateTimelineEventsClicked',
                      event,
                      statsContext                    );
                  }}
                >
                  {t('chat.answer.generateTimeline')}
                </Button>
              </TpTooltip>
              </>
              )}
              {canApplyJob && (
                <EventsReviewModal
                  isOpen={applyCardOpen}
                  onClose={() => setApplyCardOpen(false)}
                  question={question}
                  extractionJob={extractionJob}
                  setApplyCardOpen={setApplyCardOpen}
                  createEventsFromEntityEvents={createEventsFromEntityEvents}
                  getEventsFromEntityService={getEventsFromEntityService}
                />
              )}
            </div>
            {resultForFeedback?.chat_key && (
              <AnswerItemFeedbackModal
                isOpen={showFeedbackModal}
                isPositive={positiveFeedbackClicked}
                feedbackReason={feedbackReason}
                question={question}
                answerText={answerTextLocal || ''}
                onFeedbackReasonChange={setFeedbackReason}
                onCancel={cancelChatResultFeedback}
                onSubmit={sendChatResultFeedback}
              />
            )}
            {!readOnly && resultForFeedback?.chat_key && (
              <div className="flex flex-row gap-1">
                {resultForFeedback?.chat_key && !feedbackSubmitted && (
                  <TpTooltip content={t('chat.answer.positiveFeedback')} delayDuration={500} sideOffset={6}>
                    <Button iconOnly size="sm" style="text" color="light" background="inverse" aria-label={t('chat.answer.positiveFeedback')} onClick={() => handleFeedbackClick('positive')}>
                      <ThumbsUpIcon />
                    </Button>
                  </TpTooltip>
                )}
                {resultForFeedback?.chat_key && !feedbackSubmitted && (
                  <TpTooltip content={t('chat.answer.negativeFeedback')} delayDuration={500} sideOffset={6}>
                    <Button iconOnly size="sm" style="text" color="light" background="inverse" aria-label={t('chat.answer.negativeFeedback')} onClick={() => handleFeedbackClick('negative')}>
                      <ThumbsDownIcon />
                    </Button>
                  </TpTooltip>
                )}
                {resultForFeedback?.chat_key && feedbackSubmitted && (
                  <div className="flex flex-row cursor-default text-white/70">
                    {t('chat.answer.feedbackSubmitted')}
                  </div>
                )}
              </div>
            )}
          </CardFooter>
        )}
      </Card>

      {sourcesLocal?.length > 0 && ((!fixedWidthCards.answer || !fixedWidthCards.sources) && sourcesMediaOpen && !isSmallScreen) && (
        <div
          className={`resizer-h group/resizer flex flex-col items-center justify-start gap-1 self-stretch shrink-0 min-w-3 cursor-col-resize m-0 p-0 rounded-full transition-colors duration-300
            ${columnResizing ? 'text-white resizing-active' : 'text-white/10 hover:text-white'}`}
          onMouseDown={handleMouseDown}
          style={{ width: '10px', cursor: 'col-resize' }}
        >
          {/*
            Short fixed bar above the caret, then one flexible bar for the remaining
            height. `h-full` on both would resolve to the full stretched height each,
            pushing the caret out of the grip box entirely.
          */}
          <button
            type="button"
            className={`flex w-1 h-5 shrink-0 rounded-full border-0 p-0 cursor-col-resize transition-colors duration-300
              ${columnResizing ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'}`}
            aria-hidden
            tabIndex={-1}
          />
          <CaretCircleUpDownIcon size={18} weight="fill" className="rotate-90 shrink-0" />
          <button
            type="button"
            className={`flex w-1 flex-1 min-h-0 rounded-full border-0 p-0 cursor-col-resize transition-colors duration-300
              ${columnResizing ? 'bg-white' : 'bg-white/5 group-hover/resizer:bg-white'}`}
            aria-hidden
            tabIndex={-1}
          />
        </div>
      )}
      {sourcesLocal?.length > 0 && (
        <Card
          ref={sourcesCardParentRef}
          style={{
            width: rightCardWidth || '67%',
            flexGrow: rightCardWidth ? 0 : 1,
            minWidth: isSmallScreen ? '100%' : (sourcesMediaOpen ? '700px' : '300px'),
            maxWidth: sourcesMediaOpen ? '100%' : '260px',
            cursor: fixedWidthCards.sources ? 'default' : 'auto',
            // Must stay auto: `lg:self-stretch` only fills the row when the
            // cross size is auto.
            height: 'auto',
          }}
          classNames={{
            base: `box-border flex flex-col lg:flex-wrap items-start self-start lg:self-stretch min-h-0 bg-white/5 shadow-lg p-0 m-0 rounded-md
              ${sourcesMediaOpen ? 'lg:min-h-[200px] lg:flex-grow' : 'w-fit'} ${isSmallScreen ? 'items-center' : ''}`,
            body: `box-border flex min-w-0 flex-col overflow-x-hidden lg:flex-row lg:items-stretch gap-2 m-0 p-0 rounded-md min-h-0 lg:h-full`,
          }}
        >
          <CardBody>
            <Card
              classNames={{
                base: `box-border bg-transparent flex flex-col items-center border-1 border-solid border-white/0 shadow-none overflow-y-auto p-1 lg:p-4
                rounded-sm min-h-0 min-w-full lg:min-h-[300px] lg:min-w-[300px] ${isSmallScreen ? 'max-h-[200px]' : fillHeight ? 'lg:max-h-full' : 'lg:max-h-[calc(100vh-150px)]'} `,
                header: 'box-border flex flex-row gap-2 items-center p-0 m-0 pb-1 mb-2 rounded-none border-b-[3px] border-white border-double',
                body: 'box-border m-0 lg:mr-[-10px] p-0 lg:pr-[10px] min-h-0 custom-scrollbar2 overflow-y-auto',
              }}
            >
              <CardHeader>
                <ArchiveIcon size={25} weight="light" />
                <h4 className='m-0 p-0 font-light text-lg'>{t('chat.answer.sources')}</h4>
              </CardHeader>
              <CardBody>
               <div ref={sourcesListScrollRef} className={`${fillHeight ? 'max-h-full' : 'max-h-[calc(100vh-304px)]'} overflow-y-auto custom-scrollbar2`} onScroll={localSnapToTop}>
                  <ol className="flex flex-col gap-1 counter-list list-none m-0 p-0 pb-2 border-b-[3px] border-double border-white">
                    {sourcesLocal.map((source, index) => {
                      const sourceMediaType = sourcesMediaOpen
                        ? getMediaType(source, source.fileInfo)
                        : null;
                      const { showPageNumber, timeSuffix, pageLabel, pageValue } = sourceListLabelParts(source, sourceMediaType, t);
                      let attachementFileName = null;
                      let emailFileName = null;
                      if (source.sourceFilePath?.endsWith('.mbox') || source.sourceFilePath?.endsWith('.mbx')) {
                        if (source.previewFilePath) {
                          emailFileName = source.previewFilePath.split('/').pop();
                        }
                        if (!!source.analysisFilePath && source.analysisFilePath !== source.sourceFilePath) {
                          attachementFileName = source.analysisFilePath.split('/').pop();
                        }
                      }
                      const dispatchedPath = getDispatchedFilePath(source);
                      const dispatchedItemFileName =
                        dispatchedPath && dispatchedPath !== source.sourceFilePath
                          ? dispatchedPath.split('?')[0].split('/').pop() || ''
                          : '';
                      return (
                        <React.Fragment key={index}>
                          <li
                            id={`source_${source.index}`}
                            data-source-list-index={index}
                            data-testid="OpenSources"
                            key={source.index}
                            className={`max-w-[calc(100%-1px)] counter-item break-words m-0 pt-[10px] pr-0 pb-[11px] pl-[9px] flex hover:shadow-inner
                              hover:bg-white/5 rounded-md items-center justify-start text-white/70 hover:text-white cursor-pointer
                              before:content-[counter(source-counter)] before:counter-increment-[source-counter] before:w-[17px]
                              before:h-[17px] before:min-w-[17px] before:min-h-[17px] before:bg-[#2C3A4A] before:text-white
                              before:rounded-[10px] before:border before:border-transparent before:flex before:items-center
                              before:justify-center before:text-[10px] before:mr-[8px]
                              ${selectedSourceIndex === index ?
                                'before:border-1 before:border-solid before:border-white bg-white/5 border-[1px] border-solid border-white/0' :
                                'before:border-1 before:border-solid before:border-white/0 border-[1px] border-dashed border-white/5'
                              }
                            `}
                            onClick={(event) => {
                              openSources(index);
                              recordStat(
                                'OpenSourcesClicked',
                                event,
                                statsContext
                              );
                            }}
                          >
                            <div
                              className={`box-border flex flex-row gap-2 items-center justify-between px-1 rounded-sm cursor-pointer ${selectedSourceIndex === index ? 'text-white' : ''}`}
                            >
                              <label
                                className={`flex-1 pr-[10px] text-[14px] max-w-[205px] cursor-pointer answerSource_${answerIndex}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openSources(index);
                                }}
                              >
                                <span className={`break-all ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                                    {getFilename(source)}
                                    {showPageNumber ? `, ${pageLabel} ${pageValue}` : ''}
                                    {timeSuffix}
                                    {emailFileName ? `, ${emailFileName}` : ''}
                                    {attachementFileName ? `, ${attachementFileName}` : ''}
                                </span>
                                {dispatchedItemFileName && (
                                  <span className={`block break-all text-white/60 text-[12px] ${PATH_DISPLAY_WHITESPACE_CLASSNAME}`}>
                                    {t('chat.answer.item')}: {dispatchedItemFileName}
                                  </span>
                                )}
                              </label>
                              {cmDataMap.get(index)?.type ? (
                                <CsamDetectionPill type={cmDataMap.get(index)!.type} />
                              ) : null}
                            </div>
                          </li>
                        </React.Fragment>
                      )
                    })}
                  </ol>
                </div>
              </CardBody>
            </Card>

            {sourcesMediaOpen && selectedSourceIndex !== null && (() => {
              const selectedSource = sourcesForCard[selectedSourceIndex];
              if (isMobileEvidenceSource(mobileAnalysisEnabled, selectedSource)) {
                return (
                  <MobileEvidenceSourcePane
                    source={selectedSource}
                    cellId={resolveCellIdFromIdToken(idToken)}
                    accessToken={realAccessToken}
                    onClose={closeSources}
                  />
                );
              }
              // The obfuscated preview + CM header are gated on `canSeeCsamTags`;
              // the Original toggle additionally requires `canRevealOriginal`.
              const cmEntry = cmDataMap.get(selectedSourceIndex);
              const hasCm = canSeeCsamTags && !!cmEntry?.type;
              // Remount the card only when the rendered variant actually changes —
              // same condition `sourcesForCard` uses, so neither the in-flight window
              // nor a failed bypass fetch (both still showing the blurred asset)
              // churns the mount and re-requests it.
              const variantKey =
                hasCm && !effectiveShowObfuscated && cmEntry?.origUrl
                  ? `orig-${cmEntry.origUrl}`
                  : 'obf';
              return (
                <div
                  data-source-card-scrollport="true"
                  className={`${fillHeight ? 'max-h-full' : 'max-h-[calc(100vh-250px)]'} pt-[10px] overflow-y-auto custom-scrollbar2 w-full`}
                  onScroll={localSnapToTop}
                >
                  <SourceCard
                    key={`${selectedSourceIndex}-${variantKey}`}
                    fillScrollport
                    answerItemVideoLayout
                    sources={sourcesForCard}
                    selectedSourceIndex={selectedSourceIndex}
                    answerIndex={answerIndex}
                    epochTimeSecs={epochTimeSecs}
                    handleDownload={handleDownload}
                    handleAddSourceToScope={handleAddSourceToScope}
                    handleCopySourceForTimeline={handleCopySourceForTimeline}
                    handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined}
                    setIsExpanded={setIsExpanded}
                    fullScreenSource={
                      // For CM sources (preview enabled), don't pass fullScreenSource —
                      // it carries stale previewFilePath from a previous view and would
                      // override the CM-aware source in sourcesForCard[selectedSourceIndex].
                      hasCm ? undefined : fullScreenSource
                    }
                    copiedSource={copiedSource}
                    addedSource={addedSource}
                    closeSources={closeSources}
                    addToTimeline={addToTimeline}
                    readOnly={readOnly}
                    showObfuscatedPreview={hasCm ? effectiveShowObfuscated : undefined}
                    onToggleObfuscatedPreview={hasCm && canRevealOriginal ? setShowObfuscatedPreview : undefined}
                    contentModerationType={hasCm ? cmEntry?.type : undefined}
                    headerPrefix={
                      hasCm && cmEntry?.type ? (
                        <CsamDetectionPill type={cmEntry.type} />
                      ) : undefined
                    }
                    caseId={caseId}
                    chatKey={
                      typeof resultForFeedback?.chat_key === 'string'
                        ? resultForFeedback.chat_key
                        : undefined
                    }
                  />
                </div>
              );
            })()}
          </CardBody>
        </Card>
      )}

      {contentModerationAckIndex !== null && (
        <ContentModerationAckModal
          contentModerationType={cmDataMap.get(contentModerationAckIndex)?.type ?? ''}
          onCancel={() => setContentModerationAckIndex(null)}
          onAcknowledge={() => {
            const idx = contentModerationAckIndex;
            setContentModerationAckIndex(null);
            setShowObfuscatedPreview(true);
            setSelectedSourceIndex(idx);
            setSourcesMediaOpen(true);
          }}
        />
      )}
    </div>
  );
};
